// First Created - 14 Oct 2026
// Author - Atul Pandey
// Purpose - Create a Trinity Meeting Board meeting
// Updated - 14 Oct 2026 - added Daily / Weekly / Monthly recurrence
// Updated - 14 Oct 2026 - added meeting duration presets and auto end time
// Updated - 15 Oct 2026 - refined for recurrence presets
// Updated - 16 Oct 2026 - refined for additional emails
//
// Trinity Meeting Board:
// - Creates a native Google Calendar meeting / recurring series.
// - Participants are resolved from OrgTrailSessionCohort.
// - Meeting may include:
//      selected cohort(s)
//      selected individual cohort users
//      all cohort users in an organization
// - Google Calendar owns the meeting lifecycle after creation.
//
// Important:
// - Trail selection in this screen is only a cohort filtering aid.
// - The meeting itself is organization scoped.
// - Existing SlotBoard Google Calendar flow remains untouched.

import React, {
    useEffect,
    useMemo,
    useState,
} from "react"

import {
    useNavigate,
} from "react-router"

import useTitle from "@/hooks/useTitle"

import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"

import PulseLoader
    from "react-spinners/PulseLoader"

import {
    useGetOrganizationsQuery,
} from "@/pages/admin/organizations/organizationsApiSlice"

import {
    useGetTrailsQuery,
} from "@/features/trails/trailsApiSlice"

import {
    useGetOrgTrailSessionCohortsQuery,
} from "@/pages/admin/orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice"

import {
    useCreateNewTrinityMeetingMutation,
} from "@/features/trinityMeetings/trinityMeetingsApiSlice"


/*
|--------------------------------------------------------------------------
| 01 - Timezones
|--------------------------------------------------------------------------
*/

const TIMEZONE_OPTIONS =
    [

        {
            label:
                "US Pacific",

            value:
                "America/Los_Angeles",
        },

        {
            label:
                "US Mountain",

            value:
                "America/Denver",
        },

        {
            label:
                "US Central",

            value:
                "America/Chicago",
        },

        {
            label:
                "US Eastern",

            value:
                "America/New_York",
        },

        {
            label:
                "UK",

            value:
                "Europe/London",
        },

        {
            label:
                "India",

            value:
                "Asia/Kolkata",
        },

    ]


/*
|--------------------------------------------------------------------------
| 02 - Meeting Durations
|--------------------------------------------------------------------------
*/

const MEETING_DURATION_OPTIONS =
    [

        {
            label:
                "15 min",

            value:
                15,
        },

        {
            label:
                "30 min",

            value:
                30,
        },

        {
            label:
                "45 min",

            value:
                45,
        },

        {
            label:
                "60 min",

            value:
                60,
        },

        {
            label:
                "90 min",

            value:
                90,
        },

        {
            label:
                "2 hrs",

            value:
                120,
        },

        {
            label:
                "3 hrs",

            value:
                180,
        },

    ]


/*
|--------------------------------------------------------------------------
| 03 - Recurrence Frequencies
|--------------------------------------------------------------------------
*/

const RECURRENCE_FREQUENCIES =
    [

        {
            value:
                "daily",

            label:
                "Daily",
        },

        {
            value:
                "weekly",

            label:
                "Weekly",
        },

        {
            value:
                "monthly",

            label:
                "Monthly",
        },

    ]

/*
|--------------------------------------------------------------------------
| 04 - Recurrence Presets
|--------------------------------------------------------------------------
|
| Presets are display / convenience choices only.
|
| The persisted recurrence model remains:
|
| recurrenceFrequency + recurrenceInterval
|
*/

const RECURRENCE_PRESETS =
    [

        {
            label:
                "Daily",

            frequency:
                "daily",

            interval:
                1,
        },

        {
            label:
                "Weekly",

            frequency:
                "weekly",

            interval:
                1,
        },

        {
            label:
                "Biweekly",

            frequency:
                "weekly",

            interval:
                2,
        },

        {
            label:
                "Monthly",

            frequency:
                "monthly",

            interval:
                1,
        },

        {
            label:
                "Quarterly",

            frequency:
                "monthly",

            interval:
                3,
        },

        {
            label:
                "Semi Annually",

            frequency:
                "monthly",

            interval:
                6,
        },

        {
            label:
                "Annually",

            frequency:
                "monthly",

            interval:
                12,
        },

    ]

/*
|--------------------------------------------------------------------------
| 04b - Recurrence Days
|--------------------------------------------------------------------------
*/

const RECURRENCE_DAYS =
    [

        {
            code:
                "MO",

            label:
                "Mon",
        },

        {
            code:
                "TU",

            label:
                "Tue",
        },

        {
            code:
                "WE",

            label:
                "Wed",
        },

        {
            code:
                "TH",

            label:
                "Thu",
        },

        {
            code:
                "FR",

            label:
                "Fri",
        },

        {
            code:
                "SA",

            label:
                "Sat",
        },

        {
            code:
                "SU",

            label:
                "Sun",
        },

    ]


/*
|--------------------------------------------------------------------------
| 05 - Resolve Populated Or Plain ID
|--------------------------------------------------------------------------
*/

const getRefId = (
    value
) => {

    if (
        !value
    ) {

        return ""

    }


    if (
        typeof value ===
        "string"
    ) {

        return value

    }


    return (
        value?._id
        ||
        value?.id
        ||
        ""
    )

}


/*
|--------------------------------------------------------------------------
| 06 - User Display Name
|--------------------------------------------------------------------------
*/

const getUserDisplayName = (
    user
) => {

    return (
        user?.name
        ||
        user?.username
        ||
        user?.email
        ||
        "User"
    )

}


/*
|--------------------------------------------------------------------------
| 07 - Normalize RTK Entity Data
|--------------------------------------------------------------------------
*/

const normalizeEntityData = (
    data
) => {

    if (
        !data?.ids
    ) {

        return []

    }


    return data.ids
        .map(
            id =>
                data
                    ?.entities[
                    id
                ]
        )
        .filter(
            Boolean
        )

}


/*
|--------------------------------------------------------------------------
| 08 - Parse datetime-local Value
|--------------------------------------------------------------------------
|
| Treat datetime-local as a wall-clock value rather than converting it using
| the browser's timezone.
|
|--------------------------------------------------------------------------
*/

const parseLocalDateTimeParts = (
    value
) => {

    if (
        !value
    ) {

        return null

    }


    const match =
        value.match(
            /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
        )


    if (
        !match
    ) {

        return null

    }


    return {

        year:
            Number(
                match[1]
            ),

        month:
            Number(
                match[2]
            ),

        day:
            Number(
                match[3]
            ),

        hour:
            Number(
                match[4]
            ),

        minute:
            Number(
                match[5]
            ),

    }

}


/*
|--------------------------------------------------------------------------
| 09 - Format UTC Parts As datetime-local
|--------------------------------------------------------------------------
*/

const formatNaiveDateTime = (
    date
) => {

    const pad =
        value =>
            String(
                value
            )
                .padStart(
                    2,
                    "0"
                )


    return (
        `${date.getUTCFullYear()}-`
        +
        `${pad(
            date.getUTCMonth() +
            1
        )}-`
        +
        `${pad(
            date.getUTCDate()
        )}`
        +
        `T${pad(
            date.getUTCHours()
        )}:`
        +
        `${pad(
            date.getUTCMinutes()
        )}`
    )

}


/*
|--------------------------------------------------------------------------
| 10 - Add Minutes To datetime-local
|--------------------------------------------------------------------------
*/

const addMinutesToLocalDateTime = (
    value,
    minutes
) => {

    const parts =
        parseLocalDateTimeParts(
            value
        )


    const numericMinutes =
        Number(
            minutes
        )


    if (
        !parts
        ||
        !Number.isFinite(
            numericMinutes
        )
    ) {

        return ""

    }


    const date =
        new Date(
            Date.UTC(
                parts.year,
                parts.month -
                1,
                parts.day,
                parts.hour,
                parts.minute
            )
        )


    date.setUTCMinutes(
        date.getUTCMinutes()
        +
        numericMinutes
    )


    return formatNaiveDateTime(
        date
    )

}


/*
|--------------------------------------------------------------------------
| 11 - Difference Between datetime-local Values
|--------------------------------------------------------------------------
*/

const getDurationMinutes = (
    startValue,
    endValue
) => {

    const startParts =
        parseLocalDateTimeParts(
            startValue
        )


    const endParts =
        parseLocalDateTimeParts(
            endValue
        )


    if (
        !startParts
        ||
        !endParts
    ) {

        return null

    }


    const startTime =
        Date.UTC(
            startParts.year,
            startParts.month -
            1,
            startParts.day,
            startParts.hour,
            startParts.minute
        )


    const endTime =
        Date.UTC(
            endParts.year,
            endParts.month -
            1,
            endParts.day,
            endParts.hour,
            endParts.minute
        )


    const difference =
        Math.round(
            (
                endTime -
                startTime
            )
            /
            60000
        )


    if (
        difference <=
        0
    ) {

        return null

    }


    return difference

}


/*
|--------------------------------------------------------------------------
| 12 - Weekday From Start
|--------------------------------------------------------------------------
*/

const getRecurrenceDayFromStart = (
    value
) => {

    const parts =
        parseLocalDateTimeParts(
            value
        )


    if (
        !parts
    ) {

        return null

    }


    const date =
        new Date(
            Date.UTC(
                parts.year,
                parts.month -
                1,
                parts.day
            )
        )


    const dayMap =
        [
            "SU",
            "MO",
            "TU",
            "WE",
            "TH",
            "FR",
            "SA",
        ]


    return dayMap[
        date.getUTCDay()
    ]

}


/*
|--------------------------------------------------------------------------
| 13 - Day Of Month From Start
|--------------------------------------------------------------------------
*/

const getDayOfMonthFromStart = (
    value
) => {

    const parts =
        parseLocalDateTimeParts(
            value
        )


    return (
        parts
            ?.day
        ||
        null
    )

}


/*
|--------------------------------------------------------------------------
| 14 - Recurrence Unit Label
|--------------------------------------------------------------------------
*/

const getRecurrenceUnitLabel = (
    frequency,
    interval
) => {

    const numericInterval =
        Number(
            interval
        )


    if (
        frequency ===
        "daily"
    ) {

        return numericInterval ===
            1
            ?
            "day"
            :
            "days"

    }


    if (
        frequency ===
        "monthly"
    ) {

        return numericInterval ===
            1
            ?
            "month"
            :
            "months"

    }


    return numericInterval ===
        1
        ?
        "week"
        :
        "weeks"

}

/*
|--------------------------------------------------------------------------
| Recurrence Display Label
|--------------------------------------------------------------------------
*/

const getRecurrenceDisplayLabel = (
    frequency,
    interval
) => {

    const normalizedFrequency =
        frequency
        ||
        "weekly"


    const normalizedInterval =
        Number(
            interval
            ||
            1
        )


    if (
        normalizedFrequency ===
        "daily"
    ) {

        return normalizedInterval ===
            1
            ?
            "Daily"
            :
            `Every ${normalizedInterval} Days`

    }


    if (
        normalizedFrequency ===
        "weekly"
    ) {

        if (
            normalizedInterval ===
            1
        ) {

            return "Weekly"

        }


        if (
            normalizedInterval ===
            2
        ) {

            return "Biweekly"

        }


        return `Every ${normalizedInterval} Weeks`

    }


    if (
        normalizedFrequency ===
        "monthly"
    ) {

        if (
            normalizedInterval ===
            1
        ) {

            return "Monthly"

        }


        if (
            normalizedInterval ===
            3
        ) {

            return "Quarterly"

        }


        if (
            normalizedInterval ===
            6
        ) {

            return "Semi Annually"

        }


        if (
            normalizedInterval ===
            12
        ) {

            return "Annually"

        }


        return `Every ${normalizedInterval} Months`

    }


    return "Recurring"

}


/*
|--------------------------------------------------------------------------
| Monthly Week Ordinal
|--------------------------------------------------------------------------
|
| Example:
|
| 01 - 07 = first weekday occurrence
| 08 - 14 = second weekday occurrence
| 15 - 21 = third weekday occurrence
| 22 - 28 = fourth weekday occurrence
| 29 - 31 = fifth weekday occurrence
|
*/

const getMonthlyWeekOrdinal = (
    value
) => {

    const parts =
        parseLocalDateTimeParts(
            value
        )


    if (
        !parts?.day
    ) {

        return null

    }


    /*
    |--------------------------------------------------------------------------
    | Fifth Week Means Last Weekday
    |--------------------------------------------------------------------------
    |
    | Dates 29 - 31 fall in the fifth calendar week position.
    |
    | Using ordinal 5 in an RRULE would skip months that do not contain a
    | fifth occurrence of that weekday.
    |
    | Use -1 instead, meaning "last occurrence of the weekday in the month".
    |
    */

    if (
        parts.day >=
        29
    ) {

        return -1

    }


    return Math.ceil(
        parts.day /
        7
    )

}


/*
|--------------------------------------------------------------------------
| Ordinal Label
|--------------------------------------------------------------------------
*/

const getOrdinalLabel = (
    value
) => {

    const number =
        Number(
            value
        )


    if (
        number ===
        1
    ) {

        return "1st"

    }


    if (
        number ===
        2
    ) {

        return "2nd"

    }


    if (
        number ===
        3
    ) {

        return "3rd"

    }

    if (
        number ===
        -1
    ) {

        return "Last"

    }


    return `${number}th`

}


/*
|--------------------------------------------------------------------------
| Weekday Label
|--------------------------------------------------------------------------
*/

const getWeekdayLabel = (
    dayCode
) => {

    const day =
        RECURRENCE_DAYS
            .find(
                item =>
                    item.code ===
                    dayCode
            )


    return (
        day?.label
        ||
        dayCode
        ||
        "weekday"
    )

}


/*
|--------------------------------------------------------------------------
| Weekend Day
|--------------------------------------------------------------------------
*/

const isWeekendDay = (
    dayCode
) => {

    return [
        "SA",
        "SU",
    ].includes(
        dayCode
    )

}

/*
|--------------------------------------------------------------------------
| Parse Additional Invite Emails - 16 Oct 2026
|--------------------------------------------------------------------------
|
| Supports:
|
| - single email
| - comma separated emails
| - semicolon separated emails
| - newline separated emails
|
*/

const parseAdditionalInviteEmails =
    value => {

        if (
            !value
        ) {

            return []

        }


        return String(
            value
        )
            .split(
                /[,;\n]+/
            )
            .map(
                email =>
                    email
                        .trim()
                        .toLowerCase()
            )
            .filter(
                Boolean
            )

}

/*
|--------------------------------------------------------------------------
| Valid Email Format - 16 Oct 2026
|--------------------------------------------------------------------------
*/

const isValidEmail =
    value => {

        if (
            !value
        ) {

            return false

        }


        const emailPattern =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/


        return emailPattern.test(
            String(
                value
            )
                .trim()
                .toLowerCase()
        )

}


/*
|--------------------------------------------------------------------------
| 15 - Component
|--------------------------------------------------------------------------
*/

const EtCreateTrinityMeeting = ({
    setActiveAdminView,
}) => {

    // First Created - 14 Oct 2026
    // Author - Atul Pandey
    // Purpose - Create Trinity Meeting Board meeting


    useTitle(
        "Create Trinity Meeting"
    )


    const navigate =
        useNavigate()


    /*
    |--------------------------------------------------------------------------
    | 16 - Initial Form State
    |--------------------------------------------------------------------------
    */

    const initialFormState = {

        orgId:
            "",

        trailFilterId:
            "",

        meetingName:
            "",

        meetingDescription:
            "",

        selectedCohortIds:
            [],

        selectedUserIds:
            [],

        includeAllOrgCohortUsers:
            false,
        
        // 16 Oct 2026
        additionalInviteEmails:
            [],

        meetingStart:
            "",

        meetingEnd:
            "",

        meetingDurationMinutes:
            60,

        meetingTimezone:
            "America/Los_Angeles",

        recurrenceType:
            "one_time",

        recurrenceFrequency:
            "weekly",

        recurrenceInterval:
            1,

        recurrenceDaysOfWeek:
            [],

        recurrenceEndType:
            "after_occurrences",

        recurrenceEndDate:
            "",

        recurrenceOccurrenceCount:
            10,

    }


    const [
        formData,
        setFormData,
    ] =
        useState(
            initialFormState
        )


    const [
        userSearch,
        setUserSearch,
    ] =
        useState(
            ""
        )
    
    /*
    |--------------------------------------------------------------------------
    | Additional Invite Email Input - 16 Oct 2026
    |--------------------------------------------------------------------------
    */

    const [
        additionalInviteEmailInput,
        setAdditionalInviteEmailInput,
    ] =
        useState(
            ""
        )
    
    /*
    |--------------------------------------------------------------------------
    | Meeting Board Main Tab - 16 Oct 2026
    |--------------------------------------------------------------------------
    */

    const [
        activeMeetingTab,
        setActiveMeetingTab,
    ] =
        useState(
            "meeting"
        )


    /*
    |--------------------------------------------------------------------------
    | Participant Sub Tab - 16 Oct 2026
    |--------------------------------------------------------------------------
    */

    const [
        activeParticipantTab,
        setActiveParticipantTab,
    ] =
        useState(
            "all_cohorts"
        )

    /*
    |--------------------------------------------------------------------------
    | User Display Limit - 15 Oct 2026
    |--------------------------------------------------------------------------
    */

    const INITIAL_USER_DISPLAY_LIMIT =
        5


    const [
        userDisplayLimit,
        setUserDisplayLimit,
    ] =
        useState(
            INITIAL_USER_DISPLAY_LIMIT
        )
    
    const [
        userLoadNextCount,
        setUserLoadNextCount,
    ] =
        useState(
            5
        )

    /*
    |--------------------------------------------------------------------------
    | 17 - Create Meeting Mutation
    |--------------------------------------------------------------------------
    */

    const [

        createNewTrinityMeeting,

        {

            isLoading,

            isSuccess,

            isError,

            error,

        },

    ] =
        useCreateNewTrinityMeetingMutation()


    /*
    |--------------------------------------------------------------------------
    | 18 - Organizations
    |--------------------------------------------------------------------------
    */

    const {

        data:
            organizations,

        isSuccess:
            isOrganizationsSuccess,

    } =
        useGetOrganizationsQuery(
            "organizationsList",
            {

                pollingInterval:
                    60000,

                refetchOnFocus:
                    true,

                refetchOnMountOrArgChange:
                    true,

            }
        )


    /*
    |--------------------------------------------------------------------------
    | 19 - Trails
    |--------------------------------------------------------------------------
    */

    const {

        data:
            trails,

        isSuccess:
            isTrailsSuccess,

    } =
        useGetTrailsQuery(
            "trailsList",
            {

                pollingInterval:
                    60000,

                refetchOnFocus:
                    true,

                refetchOnMountOrArgChange:
                    true,

            }
        )


    /*
    |--------------------------------------------------------------------------
    | 20 - Cohorts
    |--------------------------------------------------------------------------
    */

    const {

        data:
            cohortsData,

        isSuccess:
            isCohortsSuccess,

    } =
        useGetOrgTrailSessionCohortsQuery({

            active:
                true,

        })


    /*
    |--------------------------------------------------------------------------
    | 21 - Return To Meeting Board After Success
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {

            if (
                isSuccess
            ) {

                setFormData(
                    initialFormState
                )


                setUserSearch(
                    ""
                )

                // 16 Oct 2026
                setAdditionalInviteEmailInput(
                    ""
                )

                setActiveMeetingTab(
                    "meeting"
                )

                setActiveParticipantTab(
                    "all_cohorts"
                )


                if (
                    setActiveAdminView
                ) {

                    setActiveAdminView(
                        "trinityMeetings"
                    )

                } else {

                    navigate(
                        "/trinityMeetings"
                    )

                }

            }

        },
        [
            isSuccess,
            navigate,
            setActiveAdminView,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 22 - Normalize Organizations
    |--------------------------------------------------------------------------
    */

    const organizationList =
        useMemo(
            () => {

                if (
                    !isOrganizationsSuccess
                ) {

                    return []

                }


                return normalizeEntityData(
                    organizations
                )

            },
            [
                isOrganizationsSuccess,
                organizations,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 23 - Normalize Trails
    |--------------------------------------------------------------------------
    */

    const trailList =
        useMemo(
            () => {

                if (
                    !isTrailsSuccess
                ) {

                    return []

                }


                return normalizeEntityData(
                    trails
                )

            },
            [
                isTrailsSuccess,
                trails,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 24 - Normalize Cohorts
    |--------------------------------------------------------------------------
    */

    const cohortList =
        useMemo(
            () => {

                if (
                    !isCohortsSuccess
                ) {

                    return []

                }


                return normalizeEntityData(
                    cohortsData
                )

            },
            [
                isCohortsSuccess,
                cohortsData,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 25 - Cohorts For Selected Organization
    |--------------------------------------------------------------------------
    */

    const organizationCohorts =
        useMemo(
            () => {

                if (
                    !formData.orgId
                ) {

                    return []

                }


                return cohortList
                    .filter(
                        cohort =>
                            getRefId(
                                cohort
                                    ?.orgId
                            ) ===
                            formData.orgId
                            &&
                            cohort
                                ?.active !==
                            false
                    )
                    .sort(
                        (
                            a,
                            b
                        ) => {

                            const aTrail =
                                a
                                    ?.trailId
                                    ?.trailName
                                ||
                                ""


                            const bTrail =
                                b
                                    ?.trailId
                                    ?.trailName
                                ||
                                ""


                            const trailCompare =
                                aTrail.localeCompare(
                                    bTrail
                                )


                            if (
                                trailCompare !==
                                0
                            ) {

                                return trailCompare

                            }


                            const seqA =
                                a
                                    ?.cohortSequence
                                ||
                                999999


                            const seqB =
                                b
                                    ?.cohortSequence
                                ||
                                999999


                            if (
                                seqA !==
                                seqB
                            ) {

                                return (
                                    seqA -
                                    seqB
                                )

                            }


                            return (
                                a
                                    ?.cohortName
                                ||
                                ""
                            )
                                .localeCompare(
                                    b
                                        ?.cohortName
                                    ||
                                    ""
                                )

                        }
                    )

            },
            [
                cohortList,
                formData.orgId,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 26 - Cohorts Visible Through Trail Filter
    |--------------------------------------------------------------------------
    */

    const visibleCohorts =
        useMemo(
            () => {

                if (
                    !formData.trailFilterId
                ) {

                    return organizationCohorts

                }


                return organizationCohorts
                    .filter(
                        cohort =>
                            getRefId(
                                cohort
                                    ?.trailId
                            ) ===
                            formData.trailFilterId
                    )

            },
            [
                organizationCohorts,
                formData.trailFilterId,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 27 - Trails Available Within Selected Organization
    |--------------------------------------------------------------------------
    */

    const availableTrailIds =
        useMemo(
            () => {

                return new Set(

                    organizationCohorts
                        .map(
                            cohort =>
                                getRefId(
                                    cohort
                                        ?.trailId
                                )
                        )
                        .filter(
                            Boolean
                        )

                )

            },
            [
                organizationCohorts,
            ]
        )


    const availableTrails =
        useMemo(
            () => {

                return trailList
                    .filter(
                        trail =>
                            availableTrailIds.has(
                                getRefId(
                                    trail
                                )
                            )
                    )
                    .sort(
                        (
                            a,
                            b
                        ) =>
                            (
                                a
                                    ?.trailName
                                ||
                                ""
                            )
                                .localeCompare(
                                    b
                                        ?.trailName
                                    ||
                                    ""
                                )
                    )

            },
            [
                trailList,
                availableTrailIds,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 28 - Organization Cohort Users
    |--------------------------------------------------------------------------
    */

    const organizationCohortUsers =
        useMemo(
            () => {

                const userMap =
                    new Map()


                organizationCohorts
                    .forEach(
                        cohort => {

                            const cohortUsers =
                                cohort
                                    ?.cohortUserIds
                                ||
                                []


                            cohortUsers
                                .filter(
                                    cohortUser =>
                                        cohortUser
                                            ?.userId
                                        &&
                                        cohortUser
                                            ?.active !==
                                        false
                                )
                                .forEach(
                                    cohortUser => {

                                        const user =
                                            cohortUser
                                                ?.userId


                                        const userId =
                                            getRefId(
                                                user
                                            )


                                        if (
                                            !userId
                                        ) {

                                            return

                                        }


                                        if (
                                            !userMap.has(
                                                userId
                                            )
                                        ) {

                                            userMap.set(
                                                userId,
                                                {

                                                    user,

                                                    cohortIds:
                                                        [],

                                                    trailIds:
                                                        [],

                                                }
                                            )

                                        }


                                        const entry =
                                            userMap.get(
                                                userId
                                            )


                                        const cohortId =
                                            getRefId(
                                                cohort
                                            )


                                        const trailId =
                                            getRefId(
                                                cohort
                                                    ?.trailId
                                            )


                                        if (
                                            cohortId
                                            &&
                                            !entry
                                                .cohortIds
                                                .includes(
                                                    cohortId
                                                )
                                        ) {

                                            entry
                                                .cohortIds
                                                .push(
                                                    cohortId
                                                )

                                        }


                                        if (
                                            trailId
                                            &&
                                            !entry
                                                .trailIds
                                                .includes(
                                                    trailId
                                                )
                                        ) {

                                            entry
                                                .trailIds
                                                .push(
                                                    trailId
                                                )

                                        }

                                    }
                                )

                        }
                    )


                return Array
                    .from(
                        userMap.values()
                    )
                    .sort(
                        (
                            a,
                            b
                        ) =>
                            getUserDisplayName(
                                a?.user
                            )
                                .localeCompare(
                                    getUserDisplayName(
                                        b?.user
                                    )
                                )
                    )

            },
            [
                organizationCohorts,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 29 - Search Users
    |--------------------------------------------------------------------------
    */

    const visibleUsers =
        useMemo(
            () => {

                const search =
                    userSearch
                        .trim()
                        .toLowerCase()


                if (
                    !search
                ) {

                    return organizationCohortUsers

                }


                return organizationCohortUsers
                    .filter(
                        item => {

                            const user =
                                item
                                    ?.user


                            const haystack =
                                [
                                    user?.name,
                                    user?.username,
                                    user?.email,
                                ]
                                    .filter(
                                        Boolean
                                    )
                                    .join(
                                        " "
                                    )
                                    .toLowerCase()


                            return haystack.includes(
                                search
                            )

                        }
                    )

            },
            [
                organizationCohortUsers,
                userSearch,
            ]
        )
    
    /*
    |--------------------------------------------------------------------------
    | Displayed Users - 15 Oct 2026
    |--------------------------------------------------------------------------
    |
    | visibleUsers contains the complete eligible / searched population.
    |
    | displayedUsers limits what is rendered initially so that the page
    | does not become unnecessarily long.
    |
    */

    const displayedUsers =
        useMemo(
            () => {

                return visibleUsers
                    .slice(
                        0,
                        userDisplayLimit
                    )

            },
            [
                visibleUsers,
                userDisplayLimit,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 30 - Estimated Resolved Participant IDs
    |--------------------------------------------------------------------------
    */

    const estimatedParticipantIds =
        useMemo(
            () => {

                const participantIds =
                    new Set()


                if (
                    formData
                        .includeAllOrgCohortUsers
                ) {

                    organizationCohortUsers
                        .forEach(
                            item => {

                                const userId =
                                    getRefId(
                                        item
                                            ?.user
                                    )


                                if (
                                    userId
                                ) {

                                    participantIds.add(
                                        userId
                                    )

                                }

                            }
                        )


                    return Array.from(
                        participantIds
                    )

                }


                organizationCohorts
                    .filter(
                        cohort =>
                            formData
                                .selectedCohortIds
                                .includes(
                                    getRefId(
                                        cohort
                                    )
                                )
                    )
                    .forEach(
                        cohort => {

                            (
                                cohort
                                    ?.cohortUserIds
                                ||
                                []
                            )
                                .filter(
                                    cohortUser =>
                                        cohortUser
                                            ?.userId
                                        &&
                                        cohortUser
                                            ?.active !==
                                        false
                                )
                                .forEach(
                                    cohortUser => {

                                        const userId =
                                            getRefId(
                                                cohortUser
                                                    ?.userId
                                            )


                                        if (
                                            userId
                                        ) {

                                            participantIds.add(
                                                userId
                                            )

                                        }

                                    }
                                )

                        }
                    )


                formData
                    .selectedUserIds
                    .forEach(
                        userId => {

                            if (
                                userId
                            ) {

                                participantIds.add(
                                    userId
                                )

                            }

                        }
                    )


                return Array.from(
                    participantIds
                )

            },
            [
                formData.includeAllOrgCohortUsers,
                formData.selectedCohortIds,
                formData.selectedUserIds,
                organizationCohorts,
                organizationCohortUsers,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 31 - Monthly Recurrence Day
    |--------------------------------------------------------------------------
    */

    const monthlyRecurrenceDay =
        useMemo(
            () => {

                return getDayOfMonthFromStart(
                    formData
                        .meetingStart
                )

            },
            [
                formData.meetingStart,
            ]
        )

    
    /*
    |--------------------------------------------------------------------------
    | Monthly Recurrence Week Ordinal
    |--------------------------------------------------------------------------
    */

    const monthlyRecurrenceWeekOrdinal =
        useMemo(
            () => {

                return getMonthlyWeekOrdinal(
                    formData
                        .meetingStart
                )

            },
            [
                formData.meetingStart,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 32 - Generic Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (
        e
    ) => {

        const {

            name,

            value,

        } =
            e.target


        /*
        |--------------------------------------------------------------------------
        | Meeting Start
        |--------------------------------------------------------------------------
        |
        | Start automatically calculates End using the current duration.
        |
        | Weekly:
        | - default to the weekday of Start.
        |
        | Monthly:
        | - default to the weekday of Start only when Start is Mon-Fri.
        | - if Start is Sat/Sun, do not silently select the weekend.
        |   The organizer must explicitly choose Sat/Sun if intended.
        |
        */

        if (
            name ===
            "meetingStart"
        ) {

            setFormData(
                prev => {

                    const startDay =
                        getRecurrenceDayFromStart(
                            value
                        )


                    return {

                        ...prev,

                        meetingStart:
                            value,

                        meetingEnd:
                            value
                                ?
                                addMinutesToLocalDateTime(
                                    value,
                                    prev
                                        .meetingDurationMinutes
                                )
                                :
                                "",


                        /*
                        |--------------------------------------------------------------------------
                        | Weekly Default Day
                        |--------------------------------------------------------------------------
                        */

                        ...(
                            prev
                                .recurrenceType ===
                            "recurring"
                            &&
                            prev
                                .recurrenceFrequency ===
                            "weekly"
                            &&
                            prev
                                .recurrenceDaysOfWeek
                                .length ===
                            0
                            &&
                            startDay

                                ?
                                {

                                    recurrenceDaysOfWeek:
                                        [
                                            startDay,
                                        ],

                                }

                                :
                                {}
                        ),


                        /*
                        |--------------------------------------------------------------------------
                        | Monthly Default Weekday
                        |--------------------------------------------------------------------------
                        */

                        ...(
                            prev
                                .recurrenceType ===
                            "recurring"
                            &&
                            prev
                                .recurrenceFrequency ===
                            "monthly"
                            &&
                            prev
                                .recurrenceDaysOfWeek
                                .length ===
                            0
                            &&
                            startDay
                            &&
                            !isWeekendDay(
                                startDay
                            )

                                ?
                                {

                                    recurrenceDaysOfWeek:
                                        [
                                            startDay,
                                        ],

                                }

                                :
                                {}
                        ),

                    }

                }
            )


            return

        }


        /*
        |--------------------------------------------------------------------------
        | Meeting End
        |--------------------------------------------------------------------------
        |
        | User may directly override End. When they do, recalculate Duration.
        |
        */

        if (
            name ===
            "meetingEnd"
        ) {

            setFormData(
                prev => {

                    const updatedDuration =
                        getDurationMinutes(
                            prev
                                .meetingStart,
                            value
                        )


                    return {

                        ...prev,

                        meetingEnd:
                            value,

                        ...(
                            updatedDuration
                                ?
                                {

                                    meetingDurationMinutes:
                                        updatedDuration,

                                }
                                :
                                {}
                        ),

                    }

                }
            )


            return

        }


        /*
        |--------------------------------------------------------------------------
        | Recurrence Frequency
        |--------------------------------------------------------------------------
        */

        if (
            name ===
            "recurrenceFrequency"
        ) {

            setFormData(
                prev => {

                    const startDay =
                        getRecurrenceDayFromStart(
                            prev
                                .meetingStart
                        )


                    return {

                        ...prev,

                        recurrenceFrequency:
                            value,


                        /*
                        |--------------------------------------------------------------------------
                        | Weekly
                        |--------------------------------------------------------------------------
                        |
                        | Multiple weekdays are allowed.
                        |
                        */

                        recurrenceDaysOfWeek:
                            value ===
                            "weekly"

                                ?
                                (
                                    prev
                                        .recurrenceDaysOfWeek
                                        .length >
                                    0

                                        ?
                                        prev
                                            .recurrenceDaysOfWeek

                                        :
                                        startDay
                                            ?
                                            [
                                                startDay,
                                            ]
                                            :
                                            []
                                )


                                /*
                                |--------------------------------------------------------------------------
                                | Monthly
                                |--------------------------------------------------------------------------
                                |
                                | Exactly one weekday.
                                |
                                | Default from Start only if Start is Mon-Fri.
                                |
                                */

                                :
                                value ===
                                "monthly"

                                    ?
                                    (
                                        startDay
                                        &&
                                        !isWeekendDay(
                                            startDay
                                        )

                                            ?
                                            [
                                                startDay,
                                            ]

                                            :
                                            []
                                    )

                                    :


                                    /*
                                    |--------------------------------------------------------------------------
                                    | Daily
                                    |--------------------------------------------------------------------------
                                    */

                                    [],

                    }

                }
            )


            return

        }


        /*
        |--------------------------------------------------------------------------
        | Recurrence Type
        |--------------------------------------------------------------------------
        */

        if (
            name ===
            "recurrenceType"
        ) {

            setFormData(
                prev => {

                    if (
                        value ===
                        "one_time"
                    ) {

                        return {

                            ...prev,

                            recurrenceType:
                                value,

                            recurrenceDaysOfWeek:
                                [],

                            recurrenceEndDate:
                                "",

                        }

                    }

                    const startDay =
                        getRecurrenceDayFromStart(
                            prev
                                .meetingStart
                        )


                    let recurrenceDays =
                        prev
                            .recurrenceDaysOfWeek


                    if (
                        prev
                            .recurrenceFrequency ===
                        "weekly"
                        &&
                        recurrenceDays.length ===
                        0
                        &&
                        startDay
                    ) {

                        recurrenceDays =
                            [
                                startDay,
                            ]

                    }


                    if (
                        prev
                            .recurrenceFrequency ===
                        "monthly"
                        &&
                        recurrenceDays.length ===
                        0
                        &&
                        startDay
                        &&
                        !isWeekendDay(
                            startDay
                        )
                    ) {

                        recurrenceDays =
                            [
                                startDay,
                            ]

                    }


                    return {

                        ...prev,

                        recurrenceType:
                            value,

                        recurrenceDaysOfWeek:
                            recurrenceDays,

                    }

                }
            )


            return

        }


        /*
        |--------------------------------------------------------------------------
        | General
        |--------------------------------------------------------------------------
        */

        setFormData(
            prev => ({

                ...prev,

                [name]:
                    value,


                /*
                |--------------------------------------------------------------------------
                | Organization Change
                |--------------------------------------------------------------------------
                */

                ...(name ===
                "orgId"
                    ?
                    {

                        trailFilterId:
                            "",

                        selectedCohortIds:
                            [],

                        selectedUserIds:
                            [],

                        includeAllOrgCohortUsers:
                            false,

                    }
                    :
                    {}),


                /*
                |--------------------------------------------------------------------------
                | Recurrence End Type
                |--------------------------------------------------------------------------
                */

                ...(name ===
                "recurrenceEndType"
                &&
                value ===
                "on_date"
                    ?
                    {

                        recurrenceOccurrenceCount:
                            10,

                    }
                    :
                    {}),

            })
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 33 - Duration Preset
    |--------------------------------------------------------------------------
    */

    const handleDurationPreset = (
        minutes
    ) => {

        setFormData(
            prev => ({

                ...prev,

                meetingDurationMinutes:
                    minutes,

                meetingEnd:
                    prev
                        .meetingStart
                        ?
                        addMinutesToLocalDateTime(
                            prev
                                .meetingStart,
                            minutes
                        )
                        :
                        prev
                            .meetingEnd,

            })
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 34 - Custom Duration Change
    |--------------------------------------------------------------------------
    */

    const handleDurationChange = (
        e
    ) => {

        const value =
            e
                .target
                .value


        const numericValue =
            Number(
                value
            )


        setFormData(
            prev => ({

                ...prev,

                meetingDurationMinutes:
                    value,

                meetingEnd:
                    prev
                        .meetingStart
                    &&
                    Number.isFinite(
                        numericValue
                    )
                    &&
                    numericValue >
                    0

                        ?
                        addMinutesToLocalDateTime(
                            prev
                                .meetingStart,
                            numericValue
                        )

                        :
                        prev
                            .meetingEnd,

            })
        )

    }

    /*
    |--------------------------------------------------------------------------
    | Recurrence Preset - 15 Oct 2026
    |--------------------------------------------------------------------------
    */

    const handleRecurrencePreset = (
        frequency,
        interval
    ) => {

        setFormData(
            prev => {

                const startDay =
                    getRecurrenceDayFromStart(
                        prev
                            .meetingStart
                    )


                let nextDays =
                    []


                /*
                |--------------------------------------------------------------------------
                | Weekly
                |--------------------------------------------------------------------------
                */

                if (
                    frequency ===
                    "weekly"
                ) {

                    nextDays =
                        prev
                            .recurrenceFrequency ===
                        "weekly"
                        &&
                        prev
                            .recurrenceDaysOfWeek
                            .length >
                        0

                            ?
                            prev
                                .recurrenceDaysOfWeek

                            :
                            startDay
                                ?
                                [
                                    startDay,
                                ]
                                :
                                []

                }


                /*
                |--------------------------------------------------------------------------
                | Monthly
                |--------------------------------------------------------------------------
                */

                if (
                    frequency ===
                    "monthly"
                ) {

                    nextDays =
                        prev
                            .recurrenceFrequency ===
                        "monthly"
                        &&
                        prev
                            .recurrenceDaysOfWeek
                            .length >
                        0

                            ?
                            [
                                prev
                                    .recurrenceDaysOfWeek[
                                        0
                                    ],
                            ]

                            :
                            startDay
                            &&
                            !isWeekendDay(
                                startDay
                            )

                                ?
                                [
                                    startDay,
                                ]

                                :
                                []

                }


                return {

                    ...prev,

                    recurrenceFrequency:
                        frequency,

                    recurrenceInterval:
                        interval,

                    recurrenceDaysOfWeek:
                        nextDays,

                }

            }
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 35 - Toggle All Organization Cohort Users
    |--------------------------------------------------------------------------
    */

    const handleAllOrgUsersToggle = () => {

        setFormData(
            prev => {

                const nextValue =
                    !prev
                        .includeAllOrgCohortUsers


                return {

                    ...prev,

                    includeAllOrgCohortUsers:
                        nextValue,

                    selectedCohortIds:
                        nextValue
                            ?
                            []
                            :
                            prev
                                .selectedCohortIds,

                    selectedUserIds:
                        nextValue
                            ?
                            []
                            :
                            prev
                                .selectedUserIds,

                }

            }
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 36 - Toggle Cohort
    |--------------------------------------------------------------------------
    */

    const handleCohortToggle = (
        cohortId
    ) => {

        setFormData(
            prev => {

                const exists =
                    prev
                        .selectedCohortIds
                        .includes(
                            cohortId
                        )


                return {

                    ...prev,

                    selectedCohortIds:
                        exists
                            ?
                            prev
                                .selectedCohortIds
                                .filter(
                                    selectedId =>
                                        selectedId !==
                                        cohortId
                                )
                            :
                            [
                                ...prev
                                    .selectedCohortIds,

                                cohortId,
                            ],

                }

            }
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 37 - Toggle Individual User
    |--------------------------------------------------------------------------
    */

    const handleUserToggle = (
        userId
    ) => {

        setFormData(
            prev => {

                const exists =
                    prev
                        .selectedUserIds
                        .includes(
                            userId
                        )


                return {

                    ...prev,

                    selectedUserIds:
                        exists
                            ?
                            prev
                                .selectedUserIds
                                .filter(
                                    selectedId =>
                                        selectedId !==
                                        userId
                                )
                            :
                            [
                                ...prev
                                    .selectedUserIds,

                                userId,
                            ],

                }

            }
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 38 - Toggle Recurrence Day - 15 Oct 2026
    |--------------------------------------------------------------------------
    */

    const handleRecurrenceDayToggle = (
        dayCode
    ) => {

        setFormData(
            prev => {

                const exists =
                    prev
                        .recurrenceDaysOfWeek
                        .includes(
                            dayCode
                        )


                /*
                |--------------------------------------------------------------------------
                | Monthly
                |--------------------------------------------------------------------------
                |
                | One weekday only.
                |
                */

                if (
                    prev
                        .recurrenceFrequency ===
                    "monthly"
                ) {

                    return {

                        ...prev,

                        recurrenceDaysOfWeek:
                            exists
                                ?
                                []
                                :
                                [
                                    dayCode,
                                ],

                    }

                }


                /*
                |--------------------------------------------------------------------------
                | Weekly
                |--------------------------------------------------------------------------
                |
                | Multiple weekdays allowed.
                |
                */

                return {

                    ...prev,

                    recurrenceDaysOfWeek:
                        exists

                            ?
                            prev
                                .recurrenceDaysOfWeek
                                .filter(
                                    day =>
                                        day !==
                                        dayCode
                                )

                            :
                            [
                                ...prev
                                    .recurrenceDaysOfWeek,

                                dayCode,
                            ],

                }

            }
        )

    }

    /*
    |--------------------------------------------------------------------------
    | Add Additional Invite Emails - 16 Oct 2026
    |--------------------------------------------------------------------------
    */

    const handleAddAdditionalInviteEmails =
        () => {

            const parsedEmails =
                parseAdditionalInviteEmails(
                    additionalInviteEmailInput
                )


            if (
                parsedEmails.length ===
                0
            ) {

                return

            }


            const invalidEmail =
                parsedEmails
                    .find(
                        email =>
                            !isValidEmail(
                                email
                            )
                    )


            if (
                invalidEmail
            ) {

                alert(
                    `Invalid email address: ${invalidEmail}`
                )

                return

            }


            setFormData(
                prev => {

                    const existingEmails =
                        Array.isArray(
                            prev
                                .additionalInviteEmails
                        )
                            ?
                            prev
                                .additionalInviteEmails
                            :
                            []


                    const mergedEmails =
                        [

                            ...new Set([

                                ...existingEmails,

                                ...parsedEmails,

                            ]),

                        ]


                    return {

                        ...prev,

                        additionalInviteEmails:
                            mergedEmails,

                    }

                }
            )


            setAdditionalInviteEmailInput(
                ""
            )

    }

    /*
    |--------------------------------------------------------------------------
    | Remove Additional Invite Email - 16 Oct 2026
    |--------------------------------------------------------------------------
    */

    const handleRemoveAdditionalInviteEmail =
        emailToRemove => {

            setFormData(
                prev => ({

                    ...prev,

                    additionalInviteEmails:
                        prev
                            .additionalInviteEmails
                            .filter(
                                email =>
                                    email !==
                                    emailToRemove
                            ),

                })
            )

    }

    /*
    |--------------------------------------------------------------------------
    | Additional Invite Email Key Down - 16 Oct 2026
    |--------------------------------------------------------------------------
    */

    const handleAdditionalInviteEmailKeyDown =
        event => {

            if (
                event.key ===
                "Enter"
            ) {

                event.preventDefault()


                handleAddAdditionalInviteEmails()

            }

    }


    /*
    |--------------------------------------------------------------------------
    | 39 - Clear Cohort Selection
    |--------------------------------------------------------------------------
    */

    const handleClearCohorts = () => {

        setFormData(
            prev => ({

                ...prev,

                selectedCohortIds:
                    [],

            })
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 40 - Clear User Selection
    |--------------------------------------------------------------------------
    */

    const handleClearUsers = () => {

        setFormData(
            prev => ({

                ...prev,

                selectedUserIds:
                    [],

            })
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 41 - Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async (
            e
        ) => {

            e.preventDefault()


            /*
            |--------------------------------------------------------------------------
            | Meeting Context
            |--------------------------------------------------------------------------
            */

            if (
                !formData.orgId
            ) {

                alert(
                    "Organization is required."
                )

                return

            }


            if (
                !formData
                    .meetingName
                    .trim()
            ) {

                alert(
                    "Meeting name is required."
                )

                return

            }


            /*
            |--------------------------------------------------------------------------
            | Participants
            |--------------------------------------------------------------------------
            */

            if (
                !formData
                    .includeAllOrgCohortUsers
                &&
                formData
                    .selectedCohortIds
                    .length ===
                0
                &&
                formData
                    .selectedUserIds
                    .length ===
                0
            ) {

                alert(
                    "Select at least one cohort, one participant, or all organization cohort users."
                )

                return

            }


            if (
                estimatedParticipantIds
                    .length ===
                0
            ) {

                alert(
                    "No meeting participants have been resolved from the current selection."
                )

                return

            }


            /*
            |--------------------------------------------------------------------------
            | Schedule
            |--------------------------------------------------------------------------
            */

            if (
                !formData.meetingStart
                ||
                !formData.meetingEnd
            ) {

                alert(
                    "Meeting start and end are required."
                )

                return

            }


            if (
                Number(
                    formData
                        .meetingDurationMinutes
                ) <=
                0
            ) {

                alert(
                    "Meeting duration must be greater than zero."
                )

                return

            }


            if (
                new Date(
                    formData.meetingEnd
                ) <=
                new Date(
                    formData.meetingStart
                )
            ) {

                alert(
                    "Meeting end must be after meeting start."
                )

                return

            }


            /*
            |--------------------------------------------------------------------------
            | Recurrence
            |--------------------------------------------------------------------------
            */

            if (
                formData
                    .recurrenceType ===
                "recurring"
            ) {

                if (
                    ![
                        "daily",
                        "weekly",
                        "monthly",
                    ].includes(
                        formData
                            .recurrenceFrequency
                    )
                ) {

                    alert(
                        "Select a valid recurrence frequency."
                    )

                    return

                }


                if (
                    formData
                        .recurrenceFrequency ===
                    "weekly"
                    &&
                    formData
                        .recurrenceDaysOfWeek
                        .length ===
                    0
                ) {

                    alert(
                        "Select at least one recurrence day for a weekly meeting."
                    )

                    return

                }

                if (
                    formData
                        .recurrenceFrequency ===
                    "monthly"
                    &&
                    formData
                        .recurrenceDaysOfWeek
                        .length !==
                    1
                ) {

                    alert(
                        "Select one weekday for a monthly recurrence. Saturday or Sunday may be selected intentionally when needed."
                    )

                    return

                }


                if (
                    Number(
                        formData
                            .recurrenceInterval
                    ) <
                    1
                ) {

                    alert(
                        "Recurrence interval must be at least 1."
                    )

                    return

                }


                if (
                    formData
                        .recurrenceEndType ===
                    "on_date"
                    &&
                    !formData
                        .recurrenceEndDate
                ) {

                    alert(
                        "Recurrence end date is required."
                    )

                    return

                }


                if (
                    formData
                        .recurrenceEndType ===
                    "after_occurrences"
                    &&
                    Number(
                        formData
                            .recurrenceOccurrenceCount
                    ) <
                    1
                ) {

                    alert(
                        "Occurrence count must be at least 1."
                    )

                    return

                }

            }


            /*
            |--------------------------------------------------------------------------
            | Payload
            |--------------------------------------------------------------------------
            */

            const meetingData = {

                orgId:
                    formData.orgId,

                meetingName:
                    formData
                        .meetingName
                        .trim(),

                meetingDescription:
                    formData
                        .meetingDescription,

                selectedCohortIds:
                    formData
                        .includeAllOrgCohortUsers
                        ?
                        []
                        :
                        formData
                            .selectedCohortIds,

                selectedUserIds:
                    formData
                        .includeAllOrgCohortUsers
                        ?
                        []
                        :
                        formData
                            .selectedUserIds,

                includeAllOrgCohortUsers:
                    formData
                        .includeAllOrgCohortUsers,

                meetingStart:
                    formData
                        .meetingStart,

                meetingEnd:
                    formData
                        .meetingEnd,

                meetingTimezone:
                    formData
                        .meetingTimezone,

                recurrenceType:
                    formData
                        .recurrenceType,

                recurrenceFrequency:
                    formData
                        .recurrenceType ===
                        "recurring"
                        ?
                        formData
                            .recurrenceFrequency
                        :
                        null,

                recurrenceInterval:
                    formData
                        .recurrenceType ===
                        "recurring"
                        ?
                        Number(
                            formData
                                .recurrenceInterval
                        )
                        :
                        1,

                recurrenceDaysOfWeek:
                    formData
                        .recurrenceType ===
                        "recurring"
                    &&
                    [
                        "weekly",
                        "monthly",
                    ].includes(
                        formData
                            .recurrenceFrequency
                    )

                        ?
                        formData
                            .recurrenceDaysOfWeek

                        :
                        [],

                recurrenceEndType:
                    formData
                        .recurrenceType ===
                        "recurring"
                        ?
                        formData
                            .recurrenceEndType
                        :
                        null,

                recurrenceEndDate:
                    formData
                        .recurrenceType ===
                        "recurring"
                    &&
                    formData
                        .recurrenceEndType ===
                        "on_date"

                        ?
                        formData
                            .recurrenceEndDate

                        :
                        null,

                recurrenceOccurrenceCount:
                    formData
                        .recurrenceType ===
                        "recurring"
                    &&
                    formData
                        .recurrenceEndType ===
                        "after_occurrences"

                        ?
                        Number(
                            formData
                                .recurrenceOccurrenceCount
                        )

                        :
                        null,

                googleCalendarId:
                    "primary",

            }


            try {

                const response =
                    await createNewTrinityMeeting(
                        meetingData
                    )
                        .unwrap()


                alert(
                    response
                        ?.message
                    ||
                    "Trinity meeting created successfully."
                )

            } catch (
                submitError
            ) {

                console.error(
                    submitError
                )


                alert(
                    submitError
                        ?.data
                        ?.message
                    ||
                    "Failed to create Trinity meeting."
                )

            }

        }


    /*
    |--------------------------------------------------------------------------
    | 42 - Cancel
    |--------------------------------------------------------------------------
    */

    const handleCancel = () => {

        if (
            setActiveAdminView
        ) {

            setActiveAdminView(
                "trinityMeetings"
            )

        } else {

            navigate(
                "/trinityMeetings"
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 43 - Clear
    |--------------------------------------------------------------------------
    */

    const handleClear = () => {

        setFormData(
            initialFormState
        )

        setUserSearch(
            ""
        )

        // 16 Oct 2026
        setAdditionalInviteEmailInput(
            ""
        )

        setActiveMeetingTab(
            "meeting"
        )

        setActiveParticipantTab(
            "all_cohorts"
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 44 - Organization Options
    |--------------------------------------------------------------------------
    */

    const organizationOptions =
        organizationList
            .slice()
            .sort(
                (
                    a,
                    b
                ) =>
                    (
                        a
                            ?.organizationName
                        ||
                        ""
                    )
                        .localeCompare(
                            b
                                ?.organizationName
                            ||
                            ""
                        )
            )
            .map(
                organization => {

                    const id =
                        getRefId(
                            organization
                        )


                    return (

                        <option
                            key={
                                id
                            }

                            value={
                                id
                            }
                        >
                            {
                                organization
                                    ?.organizationName
                            }
                        </option>

                    )

                }
            )


    /*
    |--------------------------------------------------------------------------
    | Selected Organization
    |--------------------------------------------------------------------------
    */

    const selectedOrganization =
        organizationList
            .find(
                organization =>
                    getRefId(
                        organization
                    ) ===
                    formData.orgId
            )


    /*
    |--------------------------------------------------------------------------
    | Estimated Trinity Participant Users
    |--------------------------------------------------------------------------
    |
    | estimatedParticipantIds already represents the current resolved Trinity
    | participant population from the frontend selection.
    |
    */

    const estimatedParticipantUsers =
        useMemo(
            () => {

                const participantIdSet =
                    new Set(
                        estimatedParticipantIds
                    )


                return organizationCohortUsers
                    .map(
                        item =>
                            item
                                ?.user
                    )
                    .filter(
                        user =>
                            participantIdSet.has(
                                getRefId(
                                    user
                                )
                            )
                    )

            },
            [
                estimatedParticipantIds,
                organizationCohortUsers,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | Estimated Trinity Invite Emails
    |--------------------------------------------------------------------------
    */

    const estimatedTrinityInviteEmails =
        useMemo(
            () => {

                return [

                    ...new Set(

                        estimatedParticipantUsers
                            .map(
                                user =>
                                    user
                                        ?.email
                            )
                            .filter(
                                Boolean
                            )
                            .map(
                                email =>
                                    String(
                                        email
                                    )
                                        .trim()
                                        .toLowerCase()
                            )

                    ),

                ]

            },
            [
                estimatedParticipantUsers,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | Estimated Final Invite Emails
    |--------------------------------------------------------------------------
    |
    | Used only for the frontend Meeting Summary.
    |
    | Backend and Google Calendar continue to perform their own authoritative
    | resolution and deduplication.
    |
    */

    const estimatedFinalInviteEmails =
        useMemo(
            () => {

                return [

                    ...new Set([

                        ...estimatedTrinityInviteEmails,

                        ...(
                            formData
                                .additionalInviteEmails
                            ||
                            []
                        )
                            .map(
                                email =>
                                    String(
                                        email
                                    )
                                        .trim()
                                        .toLowerCase()
                            )
                            .filter(
                                Boolean
                            ),

                    ]),

                ]

            },
            [
                estimatedTrinityInviteEmails,
                formData.additionalInviteEmails,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | Meeting Start / End Display
    |--------------------------------------------------------------------------
    */

    const meetingStartDisplay =
        formData.meetingStart
            ?
            formData
                .meetingStart
                .replace(
                    "T",
                    " "
                )
            :
            "Not set"


    const meetingEndDisplay =
        formData.meetingEnd
            ?
            formData
                .meetingEnd
                .replace(
                    "T",
                    " "
                )
            :
            "Not set"


    /*
    |--------------------------------------------------------------------------
    | Recurrence Summary
    |--------------------------------------------------------------------------
    */

    let recurrenceSummary =
        "One Time"


    if (
        formData
            .recurrenceType ===
        "recurring"
    ) {

        recurrenceSummary =
            getRecurrenceDisplayLabel(
                formData
                    .recurrenceFrequency,

                formData
                    .recurrenceInterval
            )


        if (
            formData
                .recurrenceFrequency ===
            "weekly"
            &&
            formData
                .recurrenceDaysOfWeek
                .length >
            0
        ) {

            recurrenceSummary +=
                ` · ${formData
                    .recurrenceDaysOfWeek
                    .map(
                        getWeekdayLabel
                    )
                    .join(
                        ", "
                    )}`

        }


        if (
            formData
                .recurrenceFrequency ===
            "monthly"
            &&
            formData
                .recurrenceDaysOfWeek
                .length ===
            1
            &&
            monthlyRecurrenceWeekOrdinal
        ) {

            recurrenceSummary +=
                ` · ${getOrdinalLabel(
                    monthlyRecurrenceWeekOrdinal
                )} ${getWeekdayLabel(
                    formData
                        .recurrenceDaysOfWeek[
                        0
                    ]
                )}`

        }


        if (
            formData
                .recurrenceEndType ===
            "after_occurrences"
        ) {

            recurrenceSummary +=
                ` · ${formData
                    .recurrenceOccurrenceCount} occurrences`

        }


        if (
            formData
                .recurrenceEndType ===
            "on_date"
            &&
            formData
                .recurrenceEndDate
        ) {

            recurrenceSummary +=
                ` · Ends ${formData
                    .recurrenceEndDate}`

        }

    }        
    

        /*
    |--------------------------------------------------------------------------
    | 45 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black"
        >

            {
                isLoading
                &&
                (

                    <PulseLoader
                        color={
                            "#ea580c"
                        }
                    />

                )
            }


            {
                isError
                &&
                (

                    <p
                        className="text-orange-700 font-semi-bold py-2"
                    >
                        {
                            error
                                ?.data
                                ?.message
                            ||
                            "Unable to create Trinity meeting."
                        }
                    </p>

                )
            }


            <form
                onSubmit={
                    handleSubmit
                }

                className="text-xs font-poppins"
            >

                {/* ---------------------------------------------------------- */}
                {/* Header / Organization                                     */}
                {/* ---------------------------------------------------------- */}

                <div
                    className="bg-white p-2 m-1 rounded"
                >

                    <div
                        className="flex justify-between items-center flex-wrap gap-2"
                    >

                        <div
                            className="flex-1 min-w-[240px]"
                        >

                            <p
                                className="p-1 m-1 font-bold font-poppins text-xl"
                            >
                                Create a Trinity Meeting
                            </p>


                            <p
                                className="text-[9px] text-gray-500 mx-2"
                            >
                                Create a native Google Calendar meeting for
                                Trinity participants and additional invitees.
                            </p>

                        </div>


                        <div
                            className="flex justify-end items-center flex-wrap min-w-[320px]"
                        >

                            <p
                                className="text-[10px] font-bold mx-2"
                            >
                                Organization
                            </p>


                            <select
                                name="orgId"

                                value={
                                    formData
                                        .orgId
                                }

                                onChange={
                                    handleChange
                                }

                                className="min-w-[260px] rounded p-2 m-1 bg-gray-100"
                            >

                                <option
                                    value=""
                                >
                                    Select Organization
                                </option>


                                {
                                    organizationOptions
                                }

                            </select>

                        </div>

                    </div>

                </div>


                {/* ---------------------------------------------------------- */}
                {/* Main Tabs                                                 */}
                {/* ---------------------------------------------------------- */}

                <div
                    className="bg-white rounded m-1 p-1"
                >

                    <div
                        className="flex justify-start items-center flex-wrap border-b border-gray-200"
                    >

                        <button
                            type="button"

                            onClick={
                                () =>
                                    setActiveMeetingTab(
                                        "meeting"
                                    )
                            }

                            className={`text-[11px] font-bold px-4 py-2 m-1 rounded-t ${
                                activeMeetingTab ===
                                "meeting"
                                    ?
                                    "bg-orange-600 text-white"
                                    :
                                    "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                        >
                            1. Meeting
                        </button>


                        <button
                            type="button"

                            onClick={
                                () =>
                                    setActiveMeetingTab(
                                        "participants"
                                    )
                            }

                            className={`text-[11px] font-bold px-4 py-2 m-1 rounded-t ${
                                activeMeetingTab ===
                                "participants"
                                    ?
                                    "bg-orange-600 text-white"
                                    :
                                    "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                        >
                            2. Participants

                            {
                                (
                                    estimatedParticipantIds
                                        .length
                                    +
                                    formData
                                        .additionalInviteEmails
                                        .length
                                ) >
                                0
                                &&
                                (

                                    <span
                                        className="ml-2 rounded-full bg-white text-orange-700 px-2 py-[1px]"
                                    >
                                        {
                                            estimatedParticipantIds
                                                .length
                                            +
                                            formData
                                                .additionalInviteEmails
                                                .length
                                        }
                                    </span>

                                )
                            }

                        </button>


                        <button
                            type="button"

                            onClick={
                                () =>
                                    setActiveMeetingTab(
                                        "schedule"
                                    )
                            }

                            className={`text-[11px] font-bold px-4 py-2 m-1 rounded-t ${
                                activeMeetingTab ===
                                "schedule"
                                    ?
                                    "bg-orange-600 text-white"
                                    :
                                    "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                        >
                            3. Schedule
                        </button>


                        <button
                            type="button"

                            onClick={
                                () =>
                                    setActiveMeetingTab(
                                        "summary"
                                    )
                            }

                            className={`text-[11px] font-bold px-4 py-2 m-1 rounded-t ${
                                activeMeetingTab ===
                                "summary"
                                    ?
                                    "bg-orange-600 text-white"
                                    :
                                    "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                        >
                            4. Meeting Summary
                        </button>

                    </div>

                </div>


                {/* ========================================================== */}
                {/* TAB 1 - MEETING                                           */}
                {/* ========================================================== */}

                {
                    activeMeetingTab ===
                    "meeting"
                    &&
                    (

                        <div
                            className="bg-white rounded m-1 p-3"
                        >

                            <div
                                className="mb-3"
                            >

                                <p
                                    className="font-bold text-sm m-1"
                                >
                                    Meeting Details
                                </p>


                                <p
                                    className="text-[9px] m-1 italic text-gray-600"
                                >
                                    Define the meeting title and the context that
                                    will appear in the Google Calendar invitation.
                                </p>

                            </div>


                            <div
                                className="grid grid-cols-1 gap-3"
                            >

                                <div>

                                    <p
                                        className="text-[10px] font-bold m-1"
                                    >
                                        Meeting Name
                                    </p>


                                    <input
                                        type="text"

                                        name="meetingName"

                                        value={
                                            formData
                                                .meetingName
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        placeholder="e.g. Catalyst Leadership Connect"

                                        className="w-full rounded p-2 m-1 bg-gray-100"
                                    />

                                </div>


                                <div>

                                    <p
                                        className="text-[10px] font-bold m-1"
                                    >
                                        Description
                                    </p>


                                    <textarea
                                        name="meetingDescription"

                                        rows="6"

                                        value={
                                            formData
                                                .meetingDescription
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        placeholder="Optional meeting context or agenda"

                                        className="w-full rounded p-2 m-1 bg-gray-100"
                                    />

                                </div>

                            </div>

                        </div>

                    )
                }


                {/* ========================================================== */}
                {/* TAB 2 - PARTICIPANTS                                      */}
                {/* ========================================================== */}

                {
                    activeMeetingTab ===
                    "participants"
                    &&
                    (

                        <div
                            className="bg-white rounded m-1 p-2"
                        >

                            {/* Participant Header */}

                            <div
                                className="flex justify-between items-center flex-wrap mb-2"
                            >

                                <div>

                                    <p
                                        className="font-bold text-sm m-1"
                                    >
                                        Participants
                                    </p>


                                    <p
                                        className="text-[9px] m-1 italic text-gray-600"
                                    >
                                        Select Trinity participants by organization,
                                        cohort or individual user, and add external
                                        email invitees when required.
                                    </p>

                                </div>


                                <div
                                    className="flex justify-end items-center flex-wrap"
                                >

                                    <p
                                        className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-1 px-3 py-1"
                                    >
                                        Trinity:{" "}

                                        <span
                                            className="font-bold"
                                        >
                                            {
                                                estimatedParticipantIds
                                                    .length
                                            }
                                        </span>
                                    </p>


                                    <p
                                        className="text-[10px] bg-gray-100 text-gray-700 rounded-full mx-1 px-3 py-1"
                                    >
                                        Additional:{" "}

                                        <span
                                            className="font-bold"
                                        >
                                            {
                                                formData
                                                    .additionalInviteEmails
                                                    .length
                                            }
                                        </span>
                                    </p>

                                </div>

                            </div>


                            {/* Participant Sub Tabs */}

                            <div
                                className="flex justify-start items-center flex-wrap bg-gray-100 rounded p-1 mb-2"
                            >

                                <button
                                    type="button"

                                    onClick={
                                        () =>
                                            setActiveParticipantTab(
                                                "all_cohorts"
                                            )
                                    }

                                    className={`text-[10px] font-bold rounded-full m-1 px-4 py-2 ${
                                        activeParticipantTab ===
                                        "all_cohorts"
                                            ?
                                            "bg-orange-600 text-white"
                                            :
                                            "bg-white text-gray-600 border border-gray-300"
                                    }`}
                                >
                                    All Cohorts
                                </button>


                                <button
                                    type="button"

                                    onClick={
                                        () =>
                                            setActiveParticipantTab(
                                                "selected_cohorts"
                                            )
                                    }

                                    className={`text-[10px] font-bold rounded-full m-1 px-4 py-2 ${
                                        activeParticipantTab ===
                                        "selected_cohorts"
                                            ?
                                            "bg-orange-600 text-white"
                                            :
                                            "bg-white text-gray-600 border border-gray-300"
                                    }`}
                                >
                                    Selected Cohorts

                                    {
                                        formData
                                            .selectedCohortIds
                                            .length >
                                        0
                                        &&
                                        (

                                            <span
                                                className="ml-2"
                                            >
                                                {
                                                    formData
                                                        .selectedCohortIds
                                                        .length
                                                }
                                            </span>

                                        )
                                    }

                                </button>


                                <button
                                    type="button"

                                    onClick={
                                        () =>
                                            setActiveParticipantTab(
                                                "individuals"
                                            )
                                    }

                                    className={`text-[10px] font-bold rounded-full m-1 px-4 py-2 ${
                                        activeParticipantTab ===
                                        "individuals"
                                            ?
                                            "bg-orange-600 text-white"
                                            :
                                            "bg-white text-gray-600 border border-gray-300"
                                    }`}
                                >
                                    Individual Participants

                                    {
                                        formData
                                            .selectedUserIds
                                            .length >
                                        0
                                        &&
                                        (

                                            <span
                                                className="ml-2"
                                            >
                                                {
                                                    formData
                                                        .selectedUserIds
                                                        .length
                                                }
                                            </span>

                                        )
                                    }

                                </button>


                                <button
                                    type="button"

                                    onClick={
                                        () =>
                                            setActiveParticipantTab(
                                                "additional_email"
                                            )
                                    }

                                    className={`text-[10px] font-bold rounded-full m-1 px-4 py-2 ${
                                        activeParticipantTab ===
                                        "additional_email"
                                            ?
                                            "bg-orange-600 text-white"
                                            :
                                            "bg-white text-gray-600 border border-gray-300"
                                    }`}
                                >
                                    Additional Email

                                    {
                                        formData
                                            .additionalInviteEmails
                                            .length >
                                        0
                                        &&
                                        (

                                            <span
                                                className="ml-2"
                                            >
                                                {
                                                    formData
                                                        .additionalInviteEmails
                                                        .length
                                                }
                                            </span>

                                        )
                                    }

                                </button>

                            </div>


                            {/* -------------------------------------------------- */}
                            {/* All Cohorts                                        */}
                            {/* -------------------------------------------------- */}

                            {
                                activeParticipantTab ===
                                "all_cohorts"
                                &&
                                (

                                    <div
                                        className="p-2"
                                    >

                                        <label
                                            className={`block rounded m-1 p-4 cursor-pointer border ${
                                                formData
                                                    .includeAllOrgCohortUsers
                                                    ?
                                                    "border-orange-600 bg-orange-50"
                                                    :
                                                    "border-gray-200 bg-gray-100"
                                            }`}
                                        >

                                            <div
                                                className="flex justify-start items-center"
                                            >

                                                <input
                                                    type="checkbox"

                                                    checked={
                                                        formData
                                                            .includeAllOrgCohortUsers
                                                    }

                                                    onChange={
                                                        handleAllOrgUsersToggle
                                                    }

                                                    disabled={
                                                        !formData
                                                            .orgId
                                                    }

                                                    className="m-1"
                                                />


                                                <div
                                                    className="mx-2"
                                                >

                                                    <p
                                                        className="text-xs font-bold"
                                                    >
                                                        All Organization Cohort Users
                                                    </p>


                                                    <p
                                                        className="text-[9px] text-gray-500"
                                                    >
                                                        Invite every resolved user across
                                                        all active cohorts and trails in
                                                        this organization.
                                                    </p>

                                                </div>

                                            </div>

                                        </label>


                                        {
                                            formData
                                                .orgId
                                            &&
                                            (

                                                <div
                                                    className="bg-gray-100 rounded m-1 p-3"
                                                >

                                                    <p
                                                        className="text-[10px] text-gray-600"
                                                    >
                                                        Organization cohort population:{" "}

                                                        <span
                                                            className="font-bold text-orange-700"
                                                        >
                                                            {
                                                                organizationCohortUsers
                                                                    .length
                                                            }
                                                        </span>
                                                    </p>

                                                </div>

                                            )
                                        }


                                        {
                                            !formData
                                                .orgId
                                            &&
                                            (

                                                <div
                                                    className="bg-gray-100 rounded m-1 p-3"
                                                >

                                                    <p
                                                        className="text-[10px] text-gray-600"
                                                    >
                                                        Select an organization above first.
                                                    </p>

                                                </div>

                                            )
                                        }

                                    </div>

                                )
                            }


                            {/* -------------------------------------------------- */}
                            {/* Selected Cohorts                                   */}
                            {/* -------------------------------------------------- */}

                            {
                                activeParticipantTab ===
                                "selected_cohorts"
                                &&
                                (

                                    <div
                                        className="p-2"
                                    >

                                        {
                                            formData
                                                .includeAllOrgCohortUsers
                                            ?
                                            (

                                                <div
                                                    className="bg-orange-50 border border-orange-200 rounded p-3 m-1"
                                                >

                                                    <p
                                                        className="text-[10px] text-orange-700"
                                                    >
                                                        All Organization Cohort Users is
                                                        currently selected. Turn it off
                                                        under the All Cohorts tab to select
                                                        specific cohorts.
                                                    </p>

                                                </div>

                                            )
                                            :
                                            (
                                                <>

                                                    <div
                                                        className="flex justify-between items-center flex-wrap"
                                                    >

                                                        <div>

                                                            <p
                                                                className="font-bold text-xs m-1"
                                                            >
                                                                Selected Cohorts
                                                            </p>


                                                            <p
                                                                className="text-[8px] m-1 italic text-gray-600"
                                                            >
                                                                Select one or more cohorts.
                                                                Trail is only a filter and
                                                                does not constrain the meeting.
                                                            </p>

                                                        </div>


                                                        <p
                                                            className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1"
                                                        >
                                                            Selected:{" "}

                                                            {
                                                                formData
                                                                    .selectedCohortIds
                                                                    .length
                                                            }
                                                        </p>

                                                    </div>


                                                    <select
                                                        name="trailFilterId"

                                                        value={
                                                            formData
                                                                .trailFilterId
                                                        }

                                                        onChange={
                                                            handleChange
                                                        }

                                                        disabled={
                                                            !formData
                                                                .orgId
                                                        }

                                                        className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                                                    >

                                                        <option
                                                            value=""
                                                        >
                                                            All Trails
                                                        </option>


                                                        {
                                                            availableTrails
                                                                .map(
                                                                    trail => {

                                                                        const trailId =
                                                                            getRefId(
                                                                                trail
                                                                            )


                                                                        return (

                                                                            <option
                                                                                key={
                                                                                    trailId
                                                                                }

                                                                                value={
                                                                                    trailId
                                                                                }
                                                                            >
                                                                                {
                                                                                    trail
                                                                                        ?.trailName
                                                                                }
                                                                            </option>

                                                                        )

                                                                    }
                                                                )
                                                        }

                                                    </select>


                                                    {
                                                        formData
                                                            .selectedCohortIds
                                                            .length >
                                                        0
                                                        &&
                                                        (

                                                            <div
                                                                className="flex justify-end items-center m-1"
                                                            >

                                                                <button
                                                                    type="button"

                                                                    onClick={
                                                                        handleClearCohorts
                                                                    }

                                                                    className="text-[9px] border border-gray-400 text-gray-600 bg-white rounded-full px-3 py-1 hover:bg-gray-600 hover:text-white"
                                                                >
                                                                    Clear Cohorts
                                                                </button>

                                                            </div>

                                                        )
                                                    }


                                                    {
                                                        !formData
                                                            .orgId
                                                        ?
                                                        (

                                                            <div
                                                                className="bg-gray-100 rounded m-1 p-2"
                                                            >

                                                                <p
                                                                    className="text-[10px] text-gray-600"
                                                                >
                                                                    Select an organization first.
                                                                </p>

                                                            </div>

                                                        )
                                                        :
                                                        (

                                                            <div
                                                                className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-80 overflow-y-auto"
                                                            >

                                                                {
                                                                    visibleCohorts
                                                                        .map(
                                                                            (
                                                                                cohort,
                                                                                index
                                                                            ) => {

                                                                                const cohortId =
                                                                                    getRefId(
                                                                                        cohort
                                                                                    )


                                                                                const isChecked =
                                                                                    formData
                                                                                        .selectedCohortIds
                                                                                        .includes(
                                                                                            cohortId
                                                                                        )


                                                                                const trailName =
                                                                                    cohort
                                                                                        ?.trailId
                                                                                        ?.trailName
                                                                                    ||
                                                                                    "Trail"


                                                                                const cohortUserCount =
                                                                                    (
                                                                                        cohort
                                                                                            ?.cohortUserIds
                                                                                        ||
                                                                                        []
                                                                                    )
                                                                                        .filter(
                                                                                            item =>
                                                                                                item
                                                                                                    ?.userId
                                                                                                &&
                                                                                                item
                                                                                                    ?.active !==
                                                                                                false
                                                                                        )
                                                                                        .length


                                                                                return (

                                                                                    <label
                                                                                        key={
                                                                                            cohortId
                                                                                            ||
                                                                                            index
                                                                                        }

                                                                                        className={`rounded m-1 p-2 cursor-pointer border ${
                                                                                            isChecked
                                                                                                ?
                                                                                                "bg-orange-50 border-orange-300"
                                                                                                :
                                                                                                "bg-gray-100 border-transparent"
                                                                                        }`}
                                                                                    >

                                                                                        <div
                                                                                            className="flex justify-start items-center"
                                                                                        >

                                                                                            <input
                                                                                                type="checkbox"

                                                                                                checked={
                                                                                                    isChecked
                                                                                                }

                                                                                                onChange={
                                                                                                    () =>
                                                                                                        handleCohortToggle(
                                                                                                            cohortId
                                                                                                        )
                                                                                                }

                                                                                                className="m-1"
                                                                                            />


                                                                                            <div
                                                                                                className="mx-1"
                                                                                            >

                                                                                                <p
                                                                                                    className="text-xs font-bold text-gray-700"
                                                                                                >
                                                                                                    {
                                                                                                        cohort
                                                                                                            ?.cohortName
                                                                                                        ||
                                                                                                        cohort
                                                                                                            ?.cohortCode
                                                                                                        ||
                                                                                                        "Cohort"
                                                                                                    }
                                                                                                </p>


                                                                                                <p
                                                                                                    className="text-[9px] text-gray-500"
                                                                                                >
                                                                                                    {
                                                                                                        trailName
                                                                                                    }

                                                                                                    {" · "}

                                                                                                    {
                                                                                                        cohortUserCount
                                                                                                    }

                                                                                                    {" users"}
                                                                                                </p>

                                                                                            </div>

                                                                                        </div>

                                                                                    </label>

                                                                                )

                                                                            }
                                                                        )
                                                                }

                                                            </div>

                                                        )
                                                    }


                                                    {
                                                        formData
                                                            .orgId
                                                        &&
                                                        visibleCohorts
                                                            .length ===
                                                        0
                                                        &&
                                                        (

                                                            <div
                                                                className="bg-gray-100 rounded m-1 p-2"
                                                            >

                                                                <p
                                                                    className="text-[10px] text-gray-600"
                                                                >
                                                                    No active cohorts found for
                                                                    this organization and trail
                                                                    filter.
                                                                </p>

                                                            </div>

                                                        )
                                                    }

                                                </>
                                            )
                                        }

                                    </div>

                                )
                            }


                            {/* -------------------------------------------------- */}
                            {/* Individual Participants                            */}
                            {/* -------------------------------------------------- */}

                            {
                                activeParticipantTab ===
                                "individuals"
                                &&
                                (

                                    <div
                                        className="p-2"
                                    >

                                        {
                                            formData
                                                .includeAllOrgCohortUsers
                                            ?
                                            (

                                                <div
                                                    className="bg-orange-50 border border-orange-200 rounded p-3 m-1"
                                                >

                                                    <p
                                                        className="text-[10px] text-orange-700"
                                                    >
                                                        All Organization Cohort Users is
                                                        currently selected. Individual
                                                        Trinity selections are unnecessary.
                                                        Turn off All Cohorts if you want
                                                        to select only specific users.
                                                    </p>

                                                </div>

                                            )
                                            :
                                            (
                                                <>

                                                    <div
                                                        className="flex justify-between items-center flex-wrap"
                                                    >

                                                        <div>

                                                            <p
                                                                className="font-bold text-xs m-1"
                                                            >
                                                                Individual Participants
                                                            </p>


                                                            <p
                                                                className="text-[8px] m-1 italic text-gray-600"
                                                            >
                                                                Add specific cohort users,
                                                                including users from cohorts
                                                                not selected above.
                                                            </p>

                                                        </div>


                                                        <div
                                                            className="flex justify-end items-center flex-wrap"
                                                        >

                                                            {
                                                                visibleUsers
                                                                    .length >
                                                                displayedUsers
                                                                    .length
                                                                &&
                                                                (

                                                                    <div
                                                                        className="flex justify-center items-center rounded-full border border-orange-500 bg-orange-50 m-1 px-2 py-1"
                                                                    >

                                                                        <button
                                                                            type="button"

                                                                            onClick={
                                                                                () =>
                                                                                    setUserDisplayLimit(
                                                                                        prev =>
                                                                                            Math.min(
                                                                                                prev
                                                                                                +
                                                                                                Number(
                                                                                                    userLoadNextCount
                                                                                                ),

                                                                                                visibleUsers
                                                                                                    .length
                                                                                            )
                                                                                    )
                                                                            }

                                                                            className="text-[10px] text-orange-700 font-bold"
                                                                        >
                                                                            Show Next
                                                                        </button>


                                                                        <input
                                                                            type="number"

                                                                            min="1"

                                                                            value={
                                                                                userLoadNextCount
                                                                            }

                                                                            onChange={
                                                                                event =>
                                                                                    setUserLoadNextCount(
                                                                                        Math.max(
                                                                                            1,

                                                                                            Number(
                                                                                                event
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                            ||
                                                                                            1
                                                                                        )
                                                                                    )
                                                                            }

                                                                            className="w-12 mx-1 px-1 py-[2px] rounded bg-white text-center text-[10px] border border-orange-200"
                                                                        />

                                                                    </div>

                                                                )
                                                            }


                                                            {
                                                                visibleUsers
                                                                    .length >
                                                                INITIAL_USER_DISPLAY_LIMIT
                                                                &&
                                                                (

                                                                    <button
                                                                        type="button"

                                                                        onClick={
                                                                            () => {

                                                                                if (
                                                                                    displayedUsers
                                                                                        .length ===
                                                                                    visibleUsers
                                                                                        .length
                                                                                ) {

                                                                                    setUserDisplayLimit(
                                                                                        INITIAL_USER_DISPLAY_LIMIT
                                                                                    )

                                                                                } else {

                                                                                    setUserDisplayLimit(
                                                                                        visibleUsers
                                                                                            .length
                                                                                    )

                                                                                }

                                                                            }
                                                                        }

                                                                        className="text-[10px] font-bold text-gray-700 bg-white border border-gray-400 rounded-full m-1 px-3 py-1 hover:bg-gray-700 hover:text-white"
                                                                    >
                                                                        {
                                                                            displayedUsers
                                                                                .length ===
                                                                            visibleUsers
                                                                                .length
                                                                                ?
                                                                                "Show Less"
                                                                                :
                                                                                "Show All"
                                                                        }
                                                                    </button>

                                                                )
                                                            }


                                                            <p
                                                                className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1"
                                                            >
                                                                Selected:{" "}

                                                                {
                                                                    formData
                                                                        .selectedUserIds
                                                                        .length
                                                                }
                                                            </p>

                                                        </div>

                                                    </div>


                                                    <input
                                                        type="text"

                                                        value={
                                                            userSearch
                                                        }

                                                        onChange={
                                                            event => {

                                                                setUserSearch(
                                                                    event
                                                                        .target
                                                                        .value
                                                                )


                                                                setUserDisplayLimit(
                                                                    INITIAL_USER_DISPLAY_LIMIT
                                                                )

                                                            }
                                                        }

                                                        disabled={
                                                            !formData
                                                                .orgId
                                                        }

                                                        placeholder="Search users by name or email"

                                                        className="w-full rounded p-2 m-1 bg-gray-100 disabled:text-gray-400"
                                                    />


                                                    {
                                                        formData
                                                            .selectedUserIds
                                                            .length >
                                                        0
                                                        &&
                                                        (

                                                            <div
                                                                className="flex justify-end items-center m-1"
                                                            >

                                                                <button
                                                                    type="button"

                                                                    onClick={
                                                                        handleClearUsers
                                                                    }

                                                                    className="text-[9px] border border-gray-400 text-gray-600 bg-white rounded-full px-3 py-1 hover:bg-gray-600 hover:text-white"
                                                                >
                                                                    Clear Users
                                                                </button>

                                                            </div>

                                                        )
                                                    }


                                                    {
                                                        formData
                                                            .orgId
                                                        &&
                                                        visibleUsers
                                                            .length >
                                                        0
                                                        &&
                                                        (

                                                            <p
                                                                className="text-[9px] text-gray-500 m-1"
                                                            >
                                                                Showing{" "}

                                                                <span
                                                                    className="font-bold"
                                                                >
                                                                    {
                                                                        displayedUsers
                                                                            .length
                                                                    }
                                                                </span>

                                                                {" of "}

                                                                <span
                                                                    className="font-bold"
                                                                >
                                                                    {
                                                                        visibleUsers
                                                                            .length
                                                                    }
                                                                </span>

                                                                {" users"}
                                                            </p>

                                                        )
                                                    }


                                                    {
                                                        formData
                                                            .orgId
                                                        &&
                                                        (

                                                            <div
                                                                className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-96 overflow-y-auto"
                                                            >

                                                                {
                                                                    displayedUsers
                                                                        .map(
                                                                            (
                                                                                item,
                                                                                index
                                                                            ) => {

                                                                                const user =
                                                                                    item
                                                                                        ?.user


                                                                                const userId =
                                                                                    getRefId(
                                                                                        user
                                                                                    )


                                                                                const isChecked =
                                                                                    formData
                                                                                        .selectedUserIds
                                                                                        .includes(
                                                                                            userId
                                                                                        )


                                                                                return (

                                                                                    <label
                                                                                        key={
                                                                                            userId
                                                                                            ||
                                                                                            index
                                                                                        }

                                                                                        className={`flex justify-between items-center rounded m-1 p-2 cursor-pointer ${
                                                                                            isChecked
                                                                                                ?
                                                                                                "bg-orange-50 border border-orange-300"
                                                                                                :
                                                                                                "bg-gray-100"
                                                                                        }`}
                                                                                    >

                                                                                        <div
                                                                                            className="flex justify-start items-center"
                                                                                        >

                                                                                            <input
                                                                                                type="checkbox"

                                                                                                checked={
                                                                                                    isChecked
                                                                                                }

                                                                                                onChange={
                                                                                                    () =>
                                                                                                        handleUserToggle(
                                                                                                            userId
                                                                                                        )
                                                                                                }

                                                                                                className="m-1"
                                                                                            />


                                                                                            <div
                                                                                                className="mx-1"
                                                                                            >

                                                                                                <p
                                                                                                    className="text-xs font-bold text-gray-700"
                                                                                                >
                                                                                                    {
                                                                                                        getUserDisplayName(
                                                                                                            user
                                                                                                        )
                                                                                                    }
                                                                                                </p>


                                                                                                {
                                                                                                    user
                                                                                                        ?.email
                                                                                                    &&
                                                                                                    (

                                                                                                        <p
                                                                                                            className="text-[9px] text-gray-500"
                                                                                                        >
                                                                                                            {
                                                                                                                user
                                                                                                                    .email
                                                                                                            }
                                                                                                        </p>

                                                                                                    )
                                                                                                }

                                                                                            </div>

                                                                                        </div>

                                                                                    </label>

                                                                                )

                                                                            }
                                                                        )
                                                                }

                                                            </div>

                                                        )
                                                    }


                                                    {
                                                        formData
                                                            .orgId
                                                        &&
                                                        visibleUsers
                                                            .length ===
                                                        0
                                                        &&
                                                        (

                                                            <div
                                                                className="bg-gray-100 rounded m-1 p-2"
                                                            >

                                                                <p
                                                                    className="text-[10px] text-gray-600"
                                                                >
                                                                    No matching cohort users found.
                                                                </p>

                                                            </div>

                                                        )
                                                    }

                                                </>
                                            )
                                        }

                                    </div>

                                )
                            }


                            {/* -------------------------------------------------- */}
                            {/* Additional Email                                   */}
                            {/* -------------------------------------------------- */}

                            {
                                activeParticipantTab ===
                                "additional_email"
                                &&
                                (

                                    <div
                                        className="p-2"
                                    >

                                        <div
                                            className="flex justify-between items-center flex-wrap"
                                        >

                                            <div>

                                                <p
                                                    className="font-bold text-xs m-1"
                                                >
                                                    Additional Email Invitees
                                                </p>


                                                <p
                                                    className="text-[8px] m-1 italic text-gray-600"
                                                >
                                                    Invite people who may not exist as
                                                    Trinity users. These invitees may be
                                                    added alongside any Trinity participant
                                                    selection, including All Cohorts.
                                                </p>

                                            </div>


                                            <p
                                                className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1"
                                            >
                                                Added:{" "}

                                                {
                                                    formData
                                                        .additionalInviteEmails
                                                        .length
                                                }
                                            </p>

                                        </div>


                                        <div
                                            className="flex justify-start items-center flex-wrap mt-2"
                                        >

                                            <input
                                                type="text"

                                                value={
                                                    additionalInviteEmailInput
                                                }

                                                onChange={
                                                    event =>
                                                        setAdditionalInviteEmailInput(
                                                            event
                                                                .target
                                                                .value
                                                        )
                                                }

                                                onKeyDown={
                                                    handleAdditionalInviteEmailKeyDown
                                                }

                                                placeholder="e.g. person@company.com"

                                                className="flex-1 min-w-[260px] rounded p-2 m-1 bg-gray-100"
                                            />


                                            <button
                                                type="button"

                                                onClick={
                                                    handleAddAdditionalInviteEmails
                                                }

                                                className="text-[10px] font-bold text-orange-700 bg-orange-50 border border-orange-500 rounded-full m-1 px-4 py-2 hover:bg-orange-600 hover:text-white"
                                            >
                                                Add
                                            </button>

                                        </div>


                                        <p
                                            className="text-[8px] text-gray-500 italic mx-1 mb-2"
                                        >
                                            Enter one or more addresses separated
                                            by commas, semicolons, or new lines.
                                        </p>


                                        {
                                            formData
                                                .additionalInviteEmails
                                                .length >
                                            0
                                            &&
                                            (

                                                <div
                                                    className="flex flex-wrap mt-2"
                                                >

                                                    {
                                                        formData
                                                            .additionalInviteEmails
                                                            .map(
                                                                email => (

                                                                    <div
                                                                        key={
                                                                            email
                                                                        }

                                                                        className="flex justify-start items-center bg-gray-100 border border-gray-300 rounded-full m-1 px-3 py-1"
                                                                    >

                                                                        <span
                                                                            className="text-[10px] text-gray-700"
                                                                        >
                                                                            {
                                                                                email
                                                                            }
                                                                        </span>


                                                                        <button
                                                                            type="button"

                                                                            onClick={
                                                                                () =>
                                                                                    handleRemoveAdditionalInviteEmail(
                                                                                        email
                                                                                    )
                                                                            }

                                                                            className="text-[11px] text-gray-500 hover:text-red-600 ml-2"
                                                                        >
                                                                            ×
                                                                        </button>

                                                                    </div>

                                                                )
                                                            )
                                                    }

                                                </div>

                                            )
                                        }

                                    </div>

                                )
                            }

                        </div>

                    )
                }


                {/* ========================================================== */}
                {/* TAB 3 - SCHEDULE                                          */}
                {/* ========================================================== */}

                {
                    activeMeetingTab ===
                    "schedule"
                    &&
                    (

                        <div
                            className="bg-white rounded m-1 p-3"
                        >

                            <div>

                                <p
                                    className="font-bold text-sm m-1"
                                >
                                    Schedule
                                </p>


                                <p
                                    className="text-[8px] m-1 italic text-gray-600"
                                >
                                    End is automatically calculated from Start and
                                    Duration. You may overwrite End directly if needed.
                                </p>

                            </div>


                            {/* Start / End */}

                            <div
                                className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2"
                            >

                                <div>

                                    <p
                                        className="text-[10px] m-1"
                                    >
                                        Start
                                    </p>


                                    <input
                                        type="datetime-local"

                                        name="meetingStart"

                                        value={
                                            formData
                                                .meetingStart
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        className="w-full rounded p-2 m-1 bg-gray-100"
                                    />

                                </div>


                                <div>

                                    <p
                                        className="text-[10px] m-1"
                                    >
                                        End
                                    </p>


                                    <input
                                        type="datetime-local"

                                        name="meetingEnd"

                                        value={
                                            formData
                                                .meetingEnd
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        className="w-full rounded p-2 m-1 bg-gray-100"
                                    />

                                </div>

                            </div>


                            {/* Duration / Timezone */}

                            <div
                                className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3"
                            >

                                <div
                                    className="bg-gray-100 rounded p-2"
                                >

                                    <div
                                        className="flex justify-between items-center flex-wrap"
                                    >

                                        <div>

                                            <p
                                                className="text-[10px] font-bold m-1"
                                            >
                                                Duration
                                            </p>


                                            <p
                                                className="text-[8px] italic text-gray-500 m-1"
                                            >
                                                Default is 60 minutes.
                                            </p>

                                        </div>


                                        <div
                                            className="flex justify-start items-center"
                                        >

                                            <input
                                                type="number"

                                                min="1"

                                                value={
                                                    formData
                                                        .meetingDurationMinutes
                                                }

                                                onChange={
                                                    handleDurationChange
                                                }

                                                className="w-24 rounded p-2 m-1 bg-white"
                                            />


                                            <p
                                                className="text-[10px] text-gray-600"
                                            >
                                                minutes
                                            </p>

                                        </div>

                                    </div>


                                    <div
                                        className="flex flex-wrap mt-2"
                                    >

                                        {
                                            MEETING_DURATION_OPTIONS
                                                .map(
                                                    duration => {

                                                        const isSelected =
                                                            Number(
                                                                formData
                                                                    .meetingDurationMinutes
                                                            ) ===
                                                            duration
                                                                .value


                                                        return (

                                                            <button
                                                                key={
                                                                    duration
                                                                        .value
                                                                }

                                                                type="button"

                                                                onClick={
                                                                    () =>
                                                                        handleDurationPreset(
                                                                            duration
                                                                                .value
                                                                        )
                                                                }

                                                                className={`text-[10px] rounded-full m-1 px-3 py-1 border ${
                                                                    isSelected
                                                                        ?
                                                                        "bg-orange-600 text-white border-orange-600"
                                                                        :
                                                                        "bg-white text-gray-600 border-gray-300"
                                                                }`}
                                                            >
                                                                {
                                                                    duration
                                                                        .label
                                                                }
                                                            </button>

                                                        )

                                                    }
                                                )
                                        }

                                    </div>

                                </div>


                                <div
                                    className="bg-gray-100 rounded p-2"
                                >

                                    <p
                                        className="text-[10px] font-bold m-1"
                                    >
                                        Timezone
                                    </p>


                                    <select
                                        name="meetingTimezone"

                                        value={
                                            formData
                                                .meetingTimezone
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        className="w-full rounded p-2 m-1 bg-white"
                                    >

                                        {
                                            TIMEZONE_OPTIONS
                                                .map(
                                                    timezone => (

                                                        <option
                                                            key={
                                                                timezone
                                                                    .value
                                                            }

                                                            value={
                                                                timezone
                                                                    .value
                                                            }
                                                        >
                                                            {
                                                                timezone
                                                                    .label
                                                            }

                                                            {" — "}

                                                            {
                                                                timezone
                                                                    .value
                                                            }
                                                        </option>

                                                    )
                                                )
                                        }

                                    </select>

                                </div>

                            </div>


                            {/* Repeat */}

                            <div
                                className="bg-gray-100 rounded mt-3 p-2"
                            >

                                <p
                                    className="font-bold text-xs m-1"
                                >
                                    Repeat
                                </p>


                                <div
                                    className="grid grid-cols-1 md:grid-cols-2 gap-2 m-1"
                                >

                                    <label
                                        className={`rounded p-3 cursor-pointer border ${
                                            formData
                                                .recurrenceType ===
                                            "one_time"
                                                ?
                                                "border-orange-600 bg-orange-50"
                                                :
                                                "border-gray-200 bg-white"
                                        }`}
                                    >

                                        <div
                                            className="flex justify-start items-center"
                                        >

                                            <input
                                                type="radio"

                                                name="recurrenceType"

                                                value="one_time"

                                                checked={
                                                    formData
                                                        .recurrenceType ===
                                                    "one_time"
                                                }

                                                onChange={
                                                    handleChange
                                                }

                                                className="m-1"
                                            />


                                            <div
                                                className="mx-1"
                                            >

                                                <p
                                                    className="text-xs font-bold"
                                                >
                                                    One Time
                                                </p>


                                                <p
                                                    className="text-[9px] text-gray-500"
                                                >
                                                    Create one Google Calendar meeting.
                                                </p>

                                            </div>

                                        </div>

                                    </label>


                                    <label
                                        className={`rounded p-3 cursor-pointer border ${
                                            formData
                                                .recurrenceType ===
                                            "recurring"
                                                ?
                                                "border-orange-600 bg-orange-50"
                                                :
                                                "border-gray-200 bg-white"
                                        }`}
                                    >

                                        <div
                                            className="flex justify-start items-center"
                                        >

                                            <input
                                                type="radio"

                                                name="recurrenceType"

                                                value="recurring"

                                                checked={
                                                    formData
                                                        .recurrenceType ===
                                                    "recurring"
                                                }

                                                onChange={
                                                    handleChange
                                                }

                                                className="m-1"
                                            />


                                            <div
                                                className="mx-1"
                                            >

                                                <p
                                                    className="text-xs font-bold"
                                                >
                                                    Recurring
                                                </p>


                                                <p
                                                    className="text-[9px] text-gray-500"
                                                >
                                                    Create a native recurring
                                                    Google Calendar series.
                                                </p>

                                            </div>

                                        </div>

                                    </label>

                                </div>

                            </div>


                            {/* Recurrence Configuration */}

                            {
                                formData
                                    .recurrenceType ===
                                "recurring"
                                &&
                                (

                                    <div
                                        className="border border-gray-200 rounded mt-3 p-2"
                                    >

                                        <div>

                                            <p
                                                className="font-bold text-xs m-1"
                                            >
                                                Recurrence
                                            </p>


                                            <p
                                                className="text-[8px] m-1 italic text-gray-600"
                                            >
                                                Set how frequently this Google Calendar
                                                meeting series should repeat.
                                            </p>

                                        </div>


                                        {/* Quick Presets */}

                                        <div
                                            className="bg-gray-100 rounded m-1 mt-3 p-2"
                                        >

                                            <p
                                                className="text-[10px] font-bold m-1"
                                            >
                                                Quick Presets
                                            </p>


                                            <div
                                                className="flex flex-wrap mt-2"
                                            >

                                                {
                                                    RECURRENCE_PRESETS
                                                        .map(
                                                            preset => {

                                                                const selected =
                                                                    formData
                                                                        .recurrenceFrequency ===
                                                                    preset
                                                                        .frequency
                                                                    &&
                                                                    Number(
                                                                        formData
                                                                            .recurrenceInterval
                                                                    ) ===
                                                                    preset
                                                                        .interval


                                                                return (

                                                                    <button
                                                                        key={
                                                                            preset
                                                                                .label
                                                                        }

                                                                        type="button"

                                                                        onClick={
                                                                            () =>
                                                                                handleRecurrencePreset(
                                                                                    preset
                                                                                        .frequency,

                                                                                    preset
                                                                                        .interval
                                                                                )
                                                                        }

                                                                        className={`text-[10px] rounded-full m-1 px-3 py-1 border ${
                                                                            selected
                                                                                ?
                                                                                "bg-orange-600 text-white border-orange-600"
                                                                                :
                                                                                "bg-white text-gray-600 border-gray-300"
                                                                        }`}
                                                                    >
                                                                        {
                                                                            preset
                                                                                .label
                                                                        }
                                                                    </button>

                                                                )

                                                            }
                                                        )
                                                }

                                            </div>

                                        </div>


                                        {/* Frequency */}

                                        <div
                                            className="grid grid-cols-1 md:grid-cols-3 gap-2 m-1 mt-3"
                                        >

                                            {
                                                RECURRENCE_FREQUENCIES
                                                    .map(
                                                        frequency => {

                                                            const selected =
                                                                formData
                                                                    .recurrenceFrequency ===
                                                                frequency
                                                                    .value


                                                            return (

                                                                <label
                                                                    key={
                                                                        frequency
                                                                            .value
                                                                    }

                                                                    className={`rounded p-3 cursor-pointer border ${
                                                                        selected
                                                                            ?
                                                                            "border-orange-600 bg-orange-50"
                                                                            :
                                                                            "border-gray-200 bg-gray-100"
                                                                    }`}
                                                                >

                                                                    <div
                                                                        className="flex justify-start items-center"
                                                                    >

                                                                        <input
                                                                            type="radio"

                                                                            name="recurrenceFrequency"

                                                                            value={
                                                                                frequency
                                                                                    .value
                                                                            }

                                                                            checked={
                                                                                selected
                                                                            }

                                                                            onChange={
                                                                                handleChange
                                                                            }

                                                                            className="m-1"
                                                                        />


                                                                        <p
                                                                            className="text-xs font-bold mx-1"
                                                                        >
                                                                            {
                                                                                frequency
                                                                                    .label
                                                                            }
                                                                        </p>

                                                                    </div>

                                                                </label>

                                                            )

                                                        }
                                                    )
                                            }

                                        </div>


                                        {/* Interval */}

                                        <div
                                            className="bg-gray-100 rounded m-1 mt-3 p-2"
                                        >

                                            <p
                                                className="text-[10px] font-bold m-1"
                                            >
                                                Repeat Every
                                            </p>


                                            <div
                                                className="flex justify-start items-center"
                                            >

                                                <input
                                                    type="number"

                                                    min="1"

                                                    name="recurrenceInterval"

                                                    value={
                                                        formData
                                                            .recurrenceInterval
                                                    }

                                                    onChange={
                                                        handleChange
                                                    }

                                                    className="w-24 rounded p-2 m-1 bg-white"
                                                />


                                                <p
                                                    className="text-xs mx-1"
                                                >
                                                    {
                                                        getRecurrenceUnitLabel(
                                                            formData
                                                                .recurrenceFrequency,

                                                            formData
                                                                .recurrenceInterval
                                                        )
                                                    }
                                                </p>

                                            </div>

                                        </div>


                                        {/* Weekly Days */}

                                        {
                                            formData
                                                .recurrenceFrequency ===
                                            "weekly"
                                            &&
                                            (

                                                <div
                                                    className="bg-gray-100 rounded m-1 mt-3 p-2"
                                                >

                                                    <p
                                                        className="text-[10px] font-bold m-1"
                                                    >
                                                        Repeat On
                                                    </p>


                                                    <div
                                                        className="flex flex-wrap mt-2"
                                                    >

                                                        {
                                                            RECURRENCE_DAYS
                                                                .map(
                                                                    day => {

                                                                        const selected =
                                                                            formData
                                                                                .recurrenceDaysOfWeek
                                                                                .includes(
                                                                                    day
                                                                                        .code
                                                                                )


                                                                        return (

                                                                            <button
                                                                                key={
                                                                                    day
                                                                                        .code
                                                                                }

                                                                                type="button"

                                                                                onClick={
                                                                                    () =>
                                                                                        handleRecurrenceDayToggle(
                                                                                            day
                                                                                                .code
                                                                                        )
                                                                                }

                                                                                className={`text-[10px] rounded-full m-1 px-3 py-1 border ${
                                                                                    selected
                                                                                        ?
                                                                                        "bg-orange-600 text-white border-orange-600"
                                                                                        :
                                                                                        "bg-white text-gray-600 border-gray-300"
                                                                                }`}
                                                                            >
                                                                                {
                                                                                    day
                                                                                        .label
                                                                                }
                                                                            </button>

                                                                        )

                                                                    }
                                                                )
                                                        }

                                                    </div>

                                                </div>

                                            )
                                        }


                                        {/* Daily */}

                                        {
                                            formData
                                                .recurrenceFrequency ===
                                            "daily"
                                            &&
                                            (

                                                <div
                                                    className="bg-gray-100 rounded m-1 mt-3 p-3"
                                                >

                                                    <p
                                                        className="text-[10px] text-gray-600"
                                                    >

                                                        <span
                                                            className="font-bold"
                                                        >
                                                            {
                                                                getRecurrenceDisplayLabel(
                                                                    "daily",

                                                                    formData
                                                                        .recurrenceInterval
                                                                )
                                                            }
                                                        </span>

                                                        {" "}from the meeting Start date.

                                                    </p>

                                                </div>

                                            )
                                        }


                                        {/* Monthly */}

                                        {
                                            formData
                                                .recurrenceFrequency ===
                                            "monthly"
                                            &&
                                            (

                                                <div
                                                    className="bg-gray-100 rounded m-1 mt-3 p-2"
                                                >

                                                    <p
                                                        className="text-[10px] font-bold m-1"
                                                    >
                                                        Repeat On
                                                    </p>


                                                    <p
                                                        className="text-[8px] italic text-gray-500 m-1"
                                                    >
                                                        Choose one weekday for the monthly
                                                        pattern. Saturday or Sunday may be
                                                        selected intentionally.
                                                    </p>


                                                    <div
                                                        className="flex flex-wrap mt-2"
                                                    >

                                                        {
                                                            RECURRENCE_DAYS
                                                                .map(
                                                                    day => {

                                                                        const selected =
                                                                            formData
                                                                                .recurrenceDaysOfWeek
                                                                                .includes(
                                                                                    day
                                                                                        .code
                                                                                )


                                                                        return (

                                                                            <button
                                                                                key={
                                                                                    day
                                                                                        .code
                                                                                }

                                                                                type="button"

                                                                                onClick={
                                                                                    () =>
                                                                                        handleRecurrenceDayToggle(
                                                                                            day
                                                                                                .code
                                                                                        )
                                                                                }

                                                                                className={`text-[10px] rounded-full m-1 px-3 py-1 border ${
                                                                                    selected
                                                                                        ?
                                                                                        "bg-orange-600 text-white border-orange-600"
                                                                                        :
                                                                                        isWeekendDay(
                                                                                            day
                                                                                                .code
                                                                                        )
                                                                                            ?
                                                                                            "bg-white text-gray-400 border-dashed border-gray-300"
                                                                                            :
                                                                                            "bg-white text-gray-600 border-gray-300"
                                                                                }`}
                                                                            >
                                                                                {
                                                                                    day
                                                                                        .label
                                                                                }
                                                                            </button>

                                                                        )

                                                                    }
                                                                )
                                                        }

                                                    </div>


                                                    {
                                                        formData
                                                            .recurrenceDaysOfWeek
                                                            .length ===
                                                        1
                                                        &&
                                                        monthlyRecurrenceWeekOrdinal
                                                        &&
                                                        (

                                                            <p
                                                                className="text-[10px] text-gray-600 mt-2 mx-1"
                                                            >

                                                                <span
                                                                    className="font-bold"
                                                                >
                                                                    {
                                                                        getRecurrenceDisplayLabel(
                                                                            "monthly",

                                                                            formData
                                                                                .recurrenceInterval
                                                                        )
                                                                    }
                                                                </span>

                                                                {" on the "}

                                                                <span
                                                                    className="font-bold"
                                                                >
                                                                    {
                                                                        getOrdinalLabel(
                                                                            monthlyRecurrenceWeekOrdinal
                                                                        )
                                                                    }

                                                                    {" "}

                                                                    {
                                                                        getWeekdayLabel(
                                                                            formData
                                                                                .recurrenceDaysOfWeek[
                                                                                0
                                                                            ]
                                                                        )
                                                                    }
                                                                </span>

                                                            </p>

                                                        )
                                                    }

                                                </div>

                                            )
                                        }


                                        {/* Recurrence End */}

                                        <div
                                            className="bg-gray-100 rounded m-1 mt-3 p-2"
                                        >

                                            <p
                                                className="text-[10px] font-bold m-1"
                                            >
                                                Ends
                                            </p>


                                            <div
                                                className="grid grid-cols-1 md:grid-cols-2 gap-2"
                                            >

                                                <label
                                                    className={`rounded p-2 cursor-pointer border ${
                                                        formData
                                                            .recurrenceEndType ===
                                                        "after_occurrences"
                                                            ?
                                                            "border-orange-600 bg-orange-50"
                                                            :
                                                            "border-gray-200 bg-white"
                                                    }`}
                                                >

                                                    <div
                                                        className="flex justify-start items-center"
                                                    >

                                                        <input
                                                            type="radio"

                                                            name="recurrenceEndType"

                                                            value="after_occurrences"

                                                            checked={
                                                                formData
                                                                    .recurrenceEndType ===
                                                                "after_occurrences"
                                                            }

                                                            onChange={
                                                                handleChange
                                                            }

                                                            className="m-1"
                                                        />


                                                        <p
                                                            className="text-xs mx-1"
                                                        >
                                                            After
                                                        </p>


                                                        <input
                                                            type="number"

                                                            min="1"

                                                            name="recurrenceOccurrenceCount"

                                                            value={
                                                                formData
                                                                    .recurrenceOccurrenceCount
                                                            }

                                                            onChange={
                                                                handleChange
                                                            }

                                                            disabled={
                                                                formData
                                                                    .recurrenceEndType !==
                                                                "after_occurrences"
                                                            }

                                                            className="w-20 rounded p-1 mx-1 bg-gray-100 disabled:text-gray-400"
                                                        />


                                                        <p
                                                            className="text-xs"
                                                        >
                                                            occurrences
                                                        </p>

                                                    </div>

                                                </label>


                                                <label
                                                    className={`rounded p-2 cursor-pointer border ${
                                                        formData
                                                            .recurrenceEndType ===
                                                        "on_date"
                                                            ?
                                                            "border-orange-600 bg-orange-50"
                                                            :
                                                            "border-gray-200 bg-white"
                                                    }`}
                                                >

                                                    <div
                                                        className="flex justify-start items-center"
                                                    >

                                                        <input
                                                            type="radio"

                                                            name="recurrenceEndType"

                                                            value="on_date"

                                                            checked={
                                                                formData
                                                                    .recurrenceEndType ===
                                                                "on_date"
                                                            }

                                                            onChange={
                                                                handleChange
                                                            }

                                                            className="m-1"
                                                        />


                                                        <p
                                                            className="text-xs mx-1"
                                                        >
                                                            On
                                                        </p>


                                                        <input
                                                            type="date"

                                                            name="recurrenceEndDate"

                                                            value={
                                                                formData
                                                                    .recurrenceEndDate
                                                            }

                                                            onChange={
                                                                handleChange
                                                            }

                                                            disabled={
                                                                formData
                                                                    .recurrenceEndType !==
                                                                "on_date"
                                                            }

                                                            className="rounded p-1 mx-1 bg-gray-100 disabled:text-gray-400"
                                                        />

                                                    </div>

                                                </label>

                                            </div>

                                        </div>

                                    </div>

                                )
                            }

                        </div>

                    )
                }


                {/* ========================================================== */}
                {/* TAB 4 - MEETING SUMMARY                                   */}
                {/* ========================================================== */}

                {
                    activeMeetingTab ===
                    "summary"
                    &&
                    (

                        <div
                            className="bg-white rounded m-1 p-3"
                        >

                            <div
                                className="flex justify-between items-start flex-wrap"
                            >

                                <div
                                    className="flex-1"
                                >

                                    <p
                                        className="font-bold text-sm m-1"
                                    >
                                        Meeting Summary
                                    </p>


                                    <p
                                        className="text-[9px] m-1 italic text-gray-600"
                                    >
                                        Review the meeting before creating the
                                        Google Calendar invitation.
                                    </p>

                                </div>


                                <div
                                    className="rounded-full border border-orange-600 bg-orange-50 px-4 py-2 m-1"
                                >

                                    <p
                                        className="text-[9px] text-orange-700"
                                    >
                                        Total Unique Invite Emails
                                    </p>


                                    <p
                                        className="text-lg text-center font-bold text-orange-600"
                                    >
                                        {
                                            estimatedFinalInviteEmails
                                                .length
                                        }
                                    </p>

                                </div>

                            </div>


                            {/* Meeting Title / Detail */}

                            <div
                                className="bg-gray-100 rounded m-1 mt-3 p-3"
                            >

                                <p
                                    className="text-[9px] text-gray-500"
                                >
                                    Meeting
                                </p>


                                <p
                                    className="text-base font-bold text-gray-800 mt-1"
                                >
                                    {
                                        formData
                                            .meetingName
                                            .trim()
                                        ||
                                        "Meeting name not set"
                                    }
                                </p>


                                {
                                    formData
                                        .meetingDescription
                                    &&
                                    (

                                        <p
                                            className="text-[10px] text-gray-600 mt-2 whitespace-pre-wrap"
                                        >
                                            {
                                                formData
                                                    .meetingDescription
                                            }
                                        </p>

                                    )
                                }

                            </div>


                            {/* Summary Cards */}

                            <div
                                className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-1 mt-3"
                            >

                                <div
                                    className="bg-gray-100 rounded m-1 p-2"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Organization
                                    </p>


                                    <p
                                        className="text-xs font-bold"
                                    >
                                        {
                                            selectedOrganization
                                                ?.organizationName
                                            ||
                                            "Not selected"
                                        }
                                    </p>

                                </div>


                                <div
                                    className="bg-gray-100 rounded m-1 p-2"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Trinity Participants
                                    </p>


                                    <p
                                        className="text-xs font-bold"
                                    >
                                        {
                                            estimatedParticipantIds
                                                .length
                                        }
                                    </p>

                                </div>


                                <div
                                    className="bg-gray-100 rounded m-1 p-2"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Additional Emails
                                    </p>


                                    <p
                                        className="text-xs font-bold"
                                    >
                                        {
                                            formData
                                                .additionalInviteEmails
                                                .length
                                        }
                                    </p>

                                </div>


                                <div
                                    className="bg-gray-100 rounded m-1 p-2"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Duration
                                    </p>


                                    <p
                                        className="text-xs font-bold"
                                    >
                                        {
                                            formData
                                                .meetingDurationMinutes
                                        }

                                        {" min"}
                                    </p>

                                </div>


                                <div
                                    className="bg-gray-100 rounded m-1 p-2"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Frequency
                                    </p>


                                    <p
                                        className="text-xs font-bold"
                                    >
                                        {
                                            recurrenceSummary
                                        }
                                    </p>

                                </div>


                                <div
                                    className="bg-gray-100 rounded m-1 p-2"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Timezone
                                    </p>


                                    <p
                                        className="text-xs font-bold"
                                    >
                                        {
                                            formData
                                                .meetingTimezone
                                        }
                                    </p>

                                </div>

                            </div>


                            {/* Timing */}

                            <div
                                className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3"
                            >

                                <div
                                    className="border border-gray-200 rounded p-3"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Starts
                                    </p>


                                    <p
                                        className="text-xs font-bold mt-1"
                                    >
                                        {
                                            meetingStartDisplay
                                        }
                                    </p>

                                </div>


                                <div
                                    className="border border-gray-200 rounded p-3"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Ends
                                    </p>


                                    <p
                                        className="text-xs font-bold mt-1"
                                    >
                                        {
                                            meetingEndDisplay
                                        }
                                    </p>

                                </div>

                            </div>


                            {/* Selection Summary */}

                            <div
                                className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3"
                            >

                                <div
                                    className="border border-gray-200 rounded p-3"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Cohort Selection
                                    </p>


                                    <p
                                        className="text-xs font-bold mt-1"
                                    >
                                        {
                                            formData
                                                .includeAllOrgCohortUsers
                                                ?
                                                "All Organization Cohort Users"
                                                :
                                                `${formData
                                                    .selectedCohortIds
                                                    .length} selected cohort(s)`
                                        }
                                    </p>

                                </div>


                                <div
                                    className="border border-gray-200 rounded p-3"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Individuals Added
                                    </p>


                                    <p
                                        className="text-xs font-bold mt-1"
                                    >
                                        {
                                            formData
                                                .selectedUserIds
                                                .length
                                        }
                                    </p>

                                </div>


                                <div
                                    className="border border-gray-200 rounded p-3"
                                >

                                    <p
                                        className="text-[9px] text-gray-500"
                                    >
                                        Meeting Type
                                    </p>


                                    <p
                                        className="text-xs font-bold mt-1"
                                    >
                                        {
                                            recurrenceSummary
                                        }
                                    </p>

                                </div>

                            </div>


                            {/* Invitee Emails */}

                            <div
                                className="bg-gray-100 rounded m-1 mt-3 p-3"
                            >

                                <div
                                    className="flex justify-between items-center flex-wrap"
                                >

                                    <div>

                                        <p
                                            className="text-xs font-bold"
                                        >
                                            Invited Attendee Emails
                                        </p>


                                        <p
                                            className="text-[8px] text-gray-500 italic mt-1"
                                        >
                                            Trinity participant emails and additional
                                            invite emails are combined and deduplicated
                                            for this preview.
                                        </p>

                                    </div>


                                    <p
                                        className="text-[10px] bg-white border border-gray-300 rounded-full px-3 py-1"
                                    >
                                        {
                                            estimatedFinalInviteEmails
                                                .length
                                        }

                                        {" unique"}
                                    </p>

                                </div>


                                {
                                    estimatedFinalInviteEmails
                                        .length >
                                    0
                                    ?
                                    (

                                        <div
                                            className="flex flex-wrap mt-3"
                                        >

                                            {
                                                estimatedFinalInviteEmails
                                                    .map(
                                                        email => {

                                                            const isAdditionalEmail =
                                                                formData
                                                                    .additionalInviteEmails
                                                                    .includes(
                                                                        email
                                                                    )


                                                            return (

                                                                <div
                                                                    key={
                                                                        email
                                                                    }

                                                                    className={`rounded-full border m-1 px-3 py-1 ${
                                                                        isAdditionalEmail
                                                                            ?
                                                                            "bg-orange-50 border-orange-300"
                                                                            :
                                                                            "bg-white border-gray-300"
                                                                    }`}
                                                                >

                                                                    <p
                                                                        className="text-[10px] text-gray-700"
                                                                    >
                                                                        {
                                                                            email
                                                                        }
                                                                    </p>

                                                                </div>

                                                            )

                                                        }
                                                    )
                                            }

                                        </div>

                                    )
                                    :
                                    (

                                        <div
                                            className="bg-white rounded mt-3 p-3"
                                        >

                                            <p
                                                className="text-[10px] text-gray-500"
                                            >
                                                No attendee email addresses selected yet.
                                            </p>

                                        </div>

                                    )
                                }

                            </div>


                            {/* Backend Note */}

                            <p
                                className="text-[8px] text-gray-500 italic m-2"
                            >
                                The backend will resolve, validate and deduplicate
                                the final attendee list again before creating the
                                Google Calendar meeting.
                            </p>


                            {/* Actions */}

                            <div
                                id="actions"

                                className="border-t border-gray-200 mt-4 pt-3"
                            >

                                <EtBtnFormBasic
                                    name={
                                        isLoading
                                            ?
                                            "Creating..."
                                            :
                                            "Create"
                                    }

                                    type={
                                        "create"
                                    }

                                    handleClick={
                                        handleSubmit
                                    }
                                />


                                <EtBtnFormBasic
                                    name={
                                        "Clear"
                                    }

                                    type={
                                        "clear"
                                    }

                                    handleClick={
                                        handleClear
                                    }
                                />


                                <EtBtnFormBasic
                                    name={
                                        "Cancel"
                                    }

                                    type={
                                        "cancel"
                                    }

                                    handleClick={
                                        handleCancel
                                    }
                                />

                            </div>

                        </div>

                    )
                }

            </form>

        </div>

    )

}


export default EtCreateTrinityMeeting